import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { useTranslation } from './i18n/LanguageContext';

/**
 * CONVERSATION CHALLENGE PLAYER
 * WhatsApp/Messenger-style chat interface for Darija learning
 * 
 * Design inspired by WhatsApp & Facebook Messenger:
 * - Clean message bubbles with tail
 * - Avatar for bot messages
 * - Gradient user messages
 * - Smooth animations
 * - Time stamps
 * - Read receipts style
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Bot configurations
const DEFAULT_BOTS = {
  female: { name: 'Fatima', icon: '👩', color: 'pink', gradient: 'from-pink-500 to-rose-500' },
  male: { name: 'Ahmed', icon: '👨', color: 'blue', gradient: 'from-blue-500 to-indigo-500' }
};

// Format time for messages
const formatTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Skeleton loader
const SkeletonCard = memo(({ isDarkMode }) => (
  <div className={`p-5 rounded-2xl animate-pulse ${isDarkMode ? 'bg-gray-800' : 'bg-white shadow-sm'}`}>
    <div className={`h-4 w-3/4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-3`} />
    <div className={`h-3 w-1/2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-2`} />
    <div className="flex gap-2 mt-3">
      <div className={`h-6 w-16 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
      <div className={`h-6 w-12 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
    </div>
  </div>
));
SkeletonCard.displayName = 'SkeletonCard';

// WhatsApp-style Message Bubble
const MessageBubble = memo(({ msg, bot, scriptType, isDarkMode, selectedBot, onPlayAudio, vocabulary = [], onWordClick, onShowAllWords, onBubbleClick, userAvatarUrl, userName, theme }) => {
  const { t } = useTranslation();
  const isUser = msg.type === 'user';
  const isSystem = msg.type === 'system';
  const isChallenge = msg.type === 'challenge';
  const time = formatTime(msg.timestamp);
  
  // Get all vocabulary words that appear in this message
  const getWordsInMessage = (text) => {
    if (!vocabulary || vocabulary.length === 0 || !text) return [];
    const wordsInMsg = [];
    const textLower = text.toLowerCase();
    vocabulary.forEach(v => {
      if (v.darija && textLower.includes(v.darija.toLowerCase())) {
        wordsInMsg.push(v);
      }
    });
    return wordsInMsg;
  };
  
  // Function to render text with clickable vocabulary words (black text, subtle underline)
  const renderTextWithVocab = (text) => {
    if (!vocabulary || vocabulary.length === 0 || !text) return text;
    
    // Build a map of words to their vocab data (case-insensitive)
    const vocabMap = {};
    vocabulary.forEach(v => {
      if (v.darija) {
        vocabMap[v.darija.toLowerCase()] = v;
      }
    });
    
    // Split text into words while preserving punctuation
    const words = text.split(/(\s+|[.,!?،؟])/);
    
    return words.map((word, idx) => {
      const cleanWord = word.replace(/[.,!?،؟]/g, '').toLowerCase();
      const vocabEntry = vocabMap[cleanWord];
      
      if (vocabEntry && onWordClick) {
        return (
          <span
            key={idx}
            onClick={(e) => {
              e.stopPropagation();
              onWordClick(vocabEntry);
            }}
            className={`cursor-pointer border-b border-dotted transition-all hover:bg-blue-100/30 ${
              isDarkMode 
                ? 'border-gray-400 hover:border-blue-400' 
                : 'border-gray-500 hover:border-blue-500'
            }`}
            title={`${vocabEntry.meaning} (${vocabEntry.phonetic})`}
          >
            {word}
          </span>
        );
      }
      return <span key={idx}>{word}</span>;
    });
  };
  
  // System message (centered)
  if (isSystem) {
    return (
      <div className="flex justify-center my-3">
        <div className={`px-4 py-2 rounded-full text-sm ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
          {msg.text}
        </div>
      </div>
    );
  }

  // AI feedback bubble — teacher/helper style
  if (msg.type === 'ai') {
    const c = 'purple';
    const colorMap = {
      purple: { bgDark: 'bg-purple-900', bgLight: 'bg-purple-100', textDark: 'text-purple-300', textLight: 'text-purple-600', bubbleDark: 'bg-purple-900/40 text-purple-200 border-purple-800/50', bubbleLight: 'bg-purple-50 text-purple-800 border-purple-200' },
      blue: { bgDark: 'bg-blue-900', bgLight: 'bg-blue-100', textDark: 'text-blue-300', textLight: 'text-blue-600', bubbleDark: 'bg-blue-900/40 text-blue-200 border-blue-800/50', bubbleLight: 'bg-blue-50 text-blue-800 border-blue-200' },
      green: { bgDark: 'bg-green-900', bgLight: 'bg-green-100', textDark: 'text-green-300', textLight: 'text-green-600', bubbleDark: 'bg-green-900/40 text-green-200 border-green-800/50', bubbleLight: 'bg-green-50 text-green-800 border-green-200' },
      amber: { bgDark: 'bg-amber-900', bgLight: 'bg-amber-100', textDark: 'text-amber-300', textLight: 'text-amber-600', bubbleDark: 'bg-amber-900/40 text-amber-200 border-amber-800/50', bubbleLight: 'bg-amber-50 text-amber-800 border-amber-200' },
      rose: { bgDark: 'bg-rose-900', bgLight: 'bg-rose-100', textDark: 'text-rose-300', textLight: 'text-rose-600', bubbleDark: 'bg-rose-900/40 text-rose-200 border-rose-800/50', bubbleLight: 'bg-rose-50 text-rose-800 border-rose-200' },
      cyan: { bgDark: 'bg-cyan-900', bgLight: 'bg-cyan-100', textDark: 'text-cyan-300', textLight: 'text-cyan-600', bubbleDark: 'bg-cyan-900/40 text-cyan-200 border-cyan-800/50', bubbleLight: 'bg-cyan-50 text-cyan-800 border-cyan-200' },
      indigo: { bgDark: 'bg-indigo-900', bgLight: 'bg-indigo-100', textDark: 'text-indigo-300', textLight: 'text-indigo-600', bubbleDark: 'bg-indigo-900/40 text-indigo-200 border-indigo-800/50', bubbleLight: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
    };
    const colors = colorMap[c] || colorMap.purple;
    return (
      <div className="flex items-start gap-2 px-4 my-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0 ${isDarkMode ? colors.bgDark + ' ' + colors.textDark : colors.bgLight + ' ' + colors.textLight}`}>
          {'🎓'}
        </div>
        <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm border ${isDarkMode ? colors.bubbleDark : colors.bubbleLight}`}>
          {msg.text}
        </div>
      </div>
    );
  }

  // Challenge card
  if (isChallenge) {
    return (
      <div className="flex justify-center my-4 px-4">
        <div className={`w-full max-w-md p-5 rounded-2xl shadow-lg ${isDarkMode 
          ? 'bg-gradient-to-br from-amber-900/80 to-orange-900/80 border border-amber-600/30' 
          : 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200'}`}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-xl shadow-md">
              🎯
            </div>
            <div>
              <span className={`font-bold text-lg ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>{t('chat_challenge')}</span>
              <span className={`ml-2 text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>+{msg.points} pts</span>
            </div>
          </div>
          <p className={`text-base ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{msg.question}</p>
        </div>
      </div>
    );
  }
  
  // Bot message (left side)
  if (!isUser) {
    // Get the correct text based on current scriptType
    const displayText = scriptType === 'arabic' && msg.text_arabic
      ? msg.text_arabic
      : (msg.text_latin || msg.text || msg.translation || '...');
    const wordsInThisMessage = getWordsInMessage(displayText);
    
    return (
      <div className="flex items-end gap-2 mb-3 px-3 sm:px-4">
        {/* Avatar */}
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-md overflow-hidden ${
          selectedBot === 'female' 
            ? 'bg-gradient-to-br from-pink-400 to-rose-500' 
            : 'bg-gradient-to-br from-blue-400 to-indigo-500'
        }`}>
          {bot.avatarUrl ? (
            <img src={bot.avatarUrl} alt={bot.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <span className="text-lg sm:text-xl">{bot.icon}</span>
          )}
        </div>
        
        {/* Message bubble - Click to see all words */}
        <div className="max-w-[75%] sm:max-w-[70%]">
          <div 
            onClick={() => onBubbleClick && onBubbleClick(displayText)}
            className={`relative px-4 py-3 rounded-2xl rounded-bl-md shadow-sm cursor-pointer transition-all hover:shadow-md ${
              isDarkMode 
                ? 'bg-gray-800 text-white hover:bg-gray-750' 
                : 'bg-white text-gray-800 border border-gray-100 hover:border-gray-200'
            }`}
          >
            {/* Bubble tail */}
            <div className={`absolute left-0 bottom-0 w-3 h-3 transform -translate-x-1 ${
              isDarkMode ? 'text-gray-800' : 'text-white'
            }`}>
              <svg viewBox="0 0 12 12" fill="currentColor">
                <path d="M12 12 L0 12 L12 0 Z" />
              </svg>
            </div>
            
            {/* Message text - with clickable vocab words */}
            <p className={`text-sm sm:text-base leading-relaxed ${scriptType === 'arabic' && msg.text_arabic && !msg.noRtl ? 'text-right' : ''}`}
               dir={scriptType === 'arabic' && msg.text_arabic && !msg.noRtl ? 'rtl' : 'ltr'}>
              {renderTextWithVocab(displayText)}
            </p>

            {/* Image attachment */}
            {msg.imageUrl && (
              <div className="mt-2 rounded-lg overflow-hidden min-h-[120px] bg-gray-100 dark:bg-gray-800">
                <img src={msg.imageUrl} alt="" className="w-full max-h-48 object-cover rounded-lg"
                  referrerPolicy="no-referrer"
                  onLoad={(e) => { e.target.parentElement.style.minHeight = 'auto'; }}
                  onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
              </div>
            )}

            {/* GIF attachment */}
            {msg.gifUrl && (
              <div className="mt-2 rounded-lg overflow-hidden min-h-[120px] bg-gray-100 dark:bg-gray-800">
                <img src={msg.gifUrl} alt="GIF" className="w-full max-h-48 object-cover rounded-lg"
                  referrerPolicy="no-referrer"
                  onLoad={(e) => { e.target.parentElement.style.minHeight = 'auto'; }}
                  onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
              </div>
            )}

            {/* Translation - show in both modes */}
            {msg.translation && (
              <p className={`text-xs mt-2 pt-2 border-t ${isDarkMode ? 'text-gray-400 border-gray-700' : 'text-gray-500 border-gray-100'}`}>
                💡 {msg.translation}
              </p>
            )}
            
            {/* Dictionary button - show all words in this bubble */}
            {wordsInThisMessage.length > 0 && onShowAllWords && (
              <button
                onClick={() => onShowAllWords(wordsInThisMessage)}
                className={`mt-2 flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full transition-all ${
                  isDarkMode 
                    ? 'bg-purple-600/20 text-purple-300 hover:bg-purple-600/30' 
                    : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                }`}
              >
                <span>📖</span> {wordsInThisMessage.length} definition
              </button>
            )}
            
            {/* Audio button */}
            {msg.audioUrl && (
              <button
                onClick={() => onPlayAudio(msg.audioUrl)}
                className={`mt-2 flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-all ${
                  isDarkMode 
                    ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30' 
                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                }`}
              >
                <span>🔊</span> Listen
              </button>
            )}
            
            {/* Time */}
            <div className={`text-[10px] mt-1 text-right ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {time}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // User message (right side)
  return (
    <div className="flex items-end justify-end gap-2 mb-3 px-3 sm:px-4">
      <div className="max-w-[75%] sm:max-w-[70%]">
        <div className={`relative px-4 py-3 rounded-2xl rounded-br-md shadow-lg text-white ${theme?.bubbleUser || 'bg-gradient-to-br from-gray-600 to-gray-700'}`}
          style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.15)' }}
        >
          <p className="text-sm sm:text-base leading-relaxed">{msg.text}</p>
          
          {/* Time and read receipt */}
          <div className="flex items-center justify-end gap-1 mt-1">
            <span className="text-[10px] text-white/70">{time}</span>
            <span className="text-white/70 text-xs">✓✓</span>
          </div>
        </div>
      </div>
      
      {/* User avatar with shadow */}
      <div 
        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ring-2 ring-white/50 ${
          isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
        }`}
        style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}
      >
        {userAvatarUrl ? (
          <img src={userAvatarUrl} alt="You" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <span className="text-sm sm:text-base font-bold">{userName?.charAt(0)?.toUpperCase() || '?'}</span>
        )}
      </div>
    </div>
  );
});
MessageBubble.displayName = 'MessageBubble';

// Typing indicator with animation
const TypingIndicator = memo(({ bot, isDarkMode, selectedBot }) => (
  <div className="flex items-end gap-2 mb-3 px-3 sm:px-4">
    <div 
      className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ring-2 ring-white/50 ${
        selectedBot === 'female' 
          ? 'bg-gradient-to-br from-pink-400 to-rose-500' 
          : 'bg-gradient-to-br from-blue-400 to-indigo-500'
      }`}
      style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}
    >
      {bot.avatarUrl ? (
        <img src={bot.avatarUrl} alt={bot.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
      ) : (
        <span className="text-lg sm:text-xl">{bot.icon}</span>
      )}
    </div>
    <div className={`px-4 py-3 rounded-2xl rounded-bl-md shadow-sm ${isDarkMode ? 'bg-gray-800' : 'bg-white border border-gray-100'}`}>
      <div className="flex gap-1 items-center h-5">
        <div className={`w-2 h-2 rounded-full animate-bounce ${isDarkMode ? 'bg-gray-400' : 'bg-gray-400'}`} style={{ animationDelay: '0ms' }} />
        <div className={`w-2 h-2 rounded-full animate-bounce ${isDarkMode ? 'bg-gray-400' : 'bg-gray-400'}`} style={{ animationDelay: '150ms' }} />
        <div className={`w-2 h-2 rounded-full animate-bounce ${isDarkMode ? 'bg-gray-400' : 'bg-gray-400'}`} style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  </div>
));
TypingIndicator.displayName = 'TypingIndicator';

// Flow card for conversation list
const FlowCard = memo(({ flow, isDarkMode, hasPaidPlan, onStart, bot }) => (
  <button
    onClick={() => onStart(flow)}
    disabled={!flow.is_free && !hasPaidPlan}
    className={`text-left p-4 sm:p-5 rounded-2xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 w-full ${
      isDarkMode 
        ? 'bg-gray-800 hover:bg-gray-750 border border-gray-700 focus:ring-blue-500' 
        : 'bg-white hover:shadow-lg border border-gray-100 shadow-sm focus:ring-blue-400'
    } ${!flow.is_free && !hasPaidPlan ? 'opacity-60 cursor-not-allowed' : ''}`}
  >
    <div className="flex items-start gap-3">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
        flow.bot_gender === 'female'
          ? 'bg-gradient-to-br from-pink-400 to-rose-500'
          : 'bg-gradient-to-br from-blue-400 to-indigo-500'
      }`}>
        <span className="text-2xl">{bot?.icon || '💬'}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {flow.name}
          </h3>
          {!flow.is_free && (
            <span className="px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs rounded-full font-bold flex-shrink-0">
              PRO
            </span>
          )}
        </div>
        <p className={`text-sm line-clamp-2 mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {flow.description}
        </p>
        <div className="flex gap-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            flow.difficulty === 'Beginner' 
              ? isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700'
              : flow.difficulty === 'Intermediate'
              ? isDarkMode ? 'bg-yellow-900/50 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
              : isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700'
          }`}>
            {flow.difficulty}
          </span>
          <span className={`px-2 py-0.5 rounded-full text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            ⏱️ {flow.estimated_duration || 5} min
          </span>
        </div>
      </div>
    </div>
  </button>
));
FlowCard.displayName = 'FlowCard';

// Available chat themes - Full UI theming with backgrounds
const CHAT_THEMES = {
  default: { 
    name: 'Classic', 
    gradient: 'from-gray-500 to-gray-600',
    gradientLight: 'from-gray-400 to-gray-500',
    headerBg: 'bg-gradient-to-r from-gray-600 to-gray-700',
    bubbleUser: 'bg-gradient-to-br from-gray-600 to-gray-700',
    buttonBg: 'bg-gray-600 hover:bg-gray-700',
    buttonSecondary: 'bg-gray-200 text-gray-700 hover:bg-gray-300',
    accent: 'text-gray-600',
    accentBg: 'bg-gray-600',
    ring: 'ring-gray-400',
    border: 'border-gray-400',
    chatBg: 'bg-gradient-to-b from-gray-100 to-gray-200',
    chatBgDark: 'bg-gradient-to-b from-gray-900 to-gray-800'
  },
  ocean: { 
    name: 'Ocean', 
    gradient: 'from-cyan-500 to-teal-600',
    gradientLight: 'from-cyan-400 to-teal-500',
    headerBg: 'bg-gradient-to-r from-cyan-600 to-teal-600',
    bubbleUser: 'bg-gradient-to-br from-cyan-500 to-teal-600',
    buttonBg: 'bg-cyan-600 hover:bg-cyan-700',
    buttonSecondary: 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200',
    accent: 'text-cyan-500',
    accentBg: 'bg-cyan-500',
    ring: 'ring-cyan-400',
    border: 'border-cyan-500',
    chatBg: 'bg-gradient-to-br from-cyan-50 via-teal-50 to-cyan-100',
    chatBgDark: 'bg-gradient-to-br from-gray-900 via-cyan-950 to-teal-950'
  },
  sunset: { 
    name: 'Sunset', 
    gradient: 'from-orange-500 to-red-600',
    gradientLight: 'from-orange-400 to-red-500',
    headerBg: 'bg-gradient-to-r from-orange-500 to-red-600',
    bubbleUser: 'bg-gradient-to-br from-orange-500 to-red-600',
    buttonBg: 'bg-orange-600 hover:bg-orange-700',
    buttonSecondary: 'bg-orange-100 text-orange-700 hover:bg-orange-200',
    accent: 'text-orange-500',
    accentBg: 'bg-orange-500',
    ring: 'ring-orange-400',
    border: 'border-orange-500',
    chatBg: 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50',
    chatBgDark: 'bg-gradient-to-br from-gray-900 via-orange-950 to-red-950'
  },
  forest: { 
    name: 'Forest', 
    gradient: 'from-green-500 to-emerald-600',
    gradientLight: 'from-green-400 to-emerald-500',
    headerBg: 'bg-gradient-to-r from-green-600 to-emerald-600',
    bubbleUser: 'bg-gradient-to-br from-green-500 to-emerald-600',
    buttonBg: 'bg-green-600 hover:bg-green-700',
    buttonSecondary: 'bg-green-100 text-green-700 hover:bg-green-200',
    accent: 'text-green-500',
    accentBg: 'bg-green-500',
    ring: 'ring-green-400',
    border: 'border-green-500',
    chatBg: 'bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50',
    chatBgDark: 'bg-gradient-to-br from-gray-900 via-green-950 to-emerald-950'
  },
  lavender: { 
    name: 'Lavender', 
    gradient: 'from-purple-500 to-violet-600',
    gradientLight: 'from-purple-400 to-violet-500',
    headerBg: 'bg-gradient-to-r from-purple-600 to-violet-600',
    bubbleUser: 'bg-gradient-to-br from-purple-500 to-violet-600',
    buttonBg: 'bg-purple-600 hover:bg-purple-700',
    buttonSecondary: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
    accent: 'text-purple-500',
    accentBg: 'bg-purple-500',
    ring: 'ring-purple-400',
    border: 'border-purple-500',
    chatBg: 'bg-gradient-to-br from-purple-50 via-violet-50 to-fuchsia-50',
    chatBgDark: 'bg-gradient-to-br from-gray-900 via-purple-950 to-violet-950'
  },
  rose: { 
    name: 'Rose', 
    gradient: 'from-pink-500 to-rose-600',
    gradientLight: 'from-pink-400 to-rose-500',
    headerBg: 'bg-gradient-to-r from-pink-600 to-rose-600',
    bubbleUser: 'bg-gradient-to-br from-pink-500 to-rose-600',
    buttonBg: 'bg-pink-600 hover:bg-pink-700',
    buttonSecondary: 'bg-pink-100 text-pink-700 hover:bg-pink-200',
    accent: 'text-pink-500',
    accentBg: 'bg-pink-500',
    ring: 'ring-pink-400',
    border: 'border-pink-500',
    chatBg: 'bg-gradient-to-br from-pink-50 via-rose-50 to-red-50',
    chatBgDark: 'bg-gradient-to-br from-gray-900 via-pink-950 to-rose-950'
  }
};

const ConversationChallengePlayer = ({
  isDarkMode = true,
  onBack,
  userToken,
  userId,
  hasPaidPlan = false,
  showNotification,
  currentUser
}) => {
  const { t } = useTranslation();
  // States
  const [view, setView] = useState('bot-select');
  const [selectedBot, setSelectedBot] = useState(null);
  const [scriptType, setScriptType] = useState('latin');
  const [bots, setBots] = useState(DEFAULT_BOTS);
  const [difficultyFilter, setDifficultyFilter] = useState('all'); // 'all', 'Beginner', 'Intermediate', 'Advanced'
  
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flowsLoading, setFlowsLoading] = useState(true);
  
  // Stories feature
  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [storyChapterIndex, setStoryChapterIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('conversations'); // 'conversations' or 'stories'
  
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [showChoices, setShowChoices] = useState(false);
  const [choices, setChoices] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [waitingForInput, setWaitingForInput] = useState(false);
  
  // Hint system
  const [showHint, setShowHint] = useState(false);
  const [currentHint, setCurrentHint] = useState('');
  const [hintUsed, setHintUsed] = useState(false);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  
  // Typing mode - always show input, toggle choices visibility
  const [showChoicesPanel, setShowChoicesPanel] = useState(false);
  const [typingHintText, setTypingHintText] = useState(''); // Shows first letters as hint
  const [correctAnswer, setCorrectAnswer] = useState(''); // Store correct answer for validation
  
  // Quiz mode at end of conversation
  const [showEndQuiz, setShowEndQuiz] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [quizScore, setQuizScore] = useState(0);
  const [quizComplete, setQuizComplete] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false); // Quiz popup modal
  const [flyingCongrats, setFlyingCongrats] = useState(false); // Flying congratulations animation

  // Smart Intent Pause - when user says greeting/thanks etc, bot responds and asks to continue
  const [intentPauseActive, setIntentPauseActive] = useState(false);
  const [pausedNodeId, setPausedNodeId] = useState(null); // Store where to resume from
  
  // Dictionary search
  const [dictSearchQuery, setDictSearchQuery] = useState('');
  const [aiDictResult, setAiDictResult] = useState(null); // AI-generated dictionary result
  const [aiDictSearching, setAiDictSearching] = useState(false); // Loading state for AI search
  
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [showAchievement, setShowAchievement] = useState(null);
  const [showVocabulary, setShowVocabulary] = useState(null);
  const [showTip, setShowTip] = useState(null);
  
  // Dictionary feature - click word to see definition (can be single word or array of words)
  const [selectedDictWord, setSelectedDictWord] = useState(null);
  const [dictWords, setDictWords] = useState([]); // Array for showing all words from a bubble
  const [isMobileDictOpen, setIsMobileDictOpen] = useState(false); // Mobile dictionary slide-in panel
  
  // Fuzzy match settings (loaded once from backend)
  const [fuzzySettings, setFuzzySettings] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/conversation-challenge/fuzzy-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setFuzzySettings(data); })
      .catch(() => {});
  }, []);

  // User customization & memory
  const [chatTheme, setChatTheme] = useState('default');
  const [userProgress, setUserProgress] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  
  // User avatar selection
  const [userGender, setUserGender] = useState('female'); // male or female — kept for trained conversation gText()

  // Derive avatar/name from account profile (transform Dropbox/local URLs like ModernLayout does)
  const rawAvatarUrl = currentUser?.avatar?.image_url || '';
  const accountAvatarUrl = rawAvatarUrl
    ? rawAvatarUrl.startsWith('/')
      ? `${API_URL}${rawAvatarUrl}`
      : rawAvatarUrl.replace('?dl=0', '?raw=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com')
    : '';
  const accountName = currentUser?.full_name || currentUser?.name || '';
  
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  
  const [conversationComplete, setConversationComplete] = useState(false);
  const [summary, setSummary] = useState([]);

  // Smart bot intents (loaded from API)
  const [intents, setIntents] = useState({});

  // AI status
  const [aiAvailable, setAiAvailable] = useState(false);

  // Trained conversations (Teachable Bot)
  const [trainedConversations, setTrainedConversations] = useState([]);
  const [trainedMode, setTrainedMode] = useState(false);
  const [currentTrainedConv, setCurrentTrainedConv] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [trainedGenderPending, setTrainedGenderPending] = useState(null); // conv waiting for gender pick
  const [trainedResponseOptions, setTrainedResponseOptions] = useState(null); // response node options
  const [listenPromptData, setListenPromptData] = useState(null); // listen prompt after correct answer
  const [stepAttempts, setStepAttempts] = useState(0); // wrong answer counter per step

  const prefersReducedMotion = useMemo(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    return false;
  }, []);

  // Get current theme colors
  const theme = useMemo(() => CHAT_THEMES[chatTheme] || CHAT_THEMES.default, [chatTheme]);

  // Load functions
  const loadBots = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/bots`);
      if (response.ok) {
        const data = await response.json();
        if (data.bots) {
          // Handle both array and object formats
          if (Array.isArray(data.bots)) {
            // Filter to only active bots and use bot.id as key (not gender)
            const activeBots = data.bots.filter(b => b.is_active !== false);
            const botsObj = {};
            activeBots.forEach(bot => {
              // Use bot.id as key to keep bots independent
              botsObj[bot.id] = bot;
            });
            if (Object.keys(botsObj).length > 0) {
              setBots(botsObj);
            }
          } else if (Object.keys(data.bots).length > 0) {
            setBots({ ...DEFAULT_BOTS, ...data.bots });
          }
        }
      }
    } catch (error) {
      console.log('Using default bots');
    }
  }, []);


  const loadFlows = useCallback(async () => {
    setFlowsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/flows`);
      if (response.ok) {
        const data = await response.json();
        setFlows(data.flows || []);
      }
    } catch (error) {
      console.error('Error loading flows:', error);
    } finally {
      setFlowsLoading(false);
      setLoading(false);
    }
  }, []);

  // Load stories
  const loadStories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/stories`);
      if (response.ok) {
        const data = await response.json();
        setStories(data.stories || []);
      }
    } catch (error) {
      console.error('Error loading stories:', error);
    }
  }, []);

  // Load trained conversations
  const loadTrainedConversations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/trained-conversations`);
      if (response.ok) {
        const data = await response.json();
        setTrainedConversations((data.conversations || []).filter(c => c.is_active));
      }
    } catch (error) {
      console.error('Error loading trained conversations:', error);
    }
  }, []);

  // Load smart bot intents from API
  const loadIntents = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/intents`);
      if (response.ok) {
        const data = await response.json();
        // Convert array to object keyed by intent name for faster lookup
        const intentsObj = {};
        (data.intents || []).forEach(intent => {
          if (intent.is_active) {
            intentsObj[intent.name] = intent.phrases || [];
          }
        });
        setIntents(intentsObj);
        console.log('Loaded intents from API:', Object.keys(intentsObj));
      }
    } catch (error) {
      console.error('Error loading intents:', error);
    }
  }, []);

  // Check AI service availability
  const checkAiStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/ai-status`);
      if (response.ok) {
        const data = await response.json();
        setAiAvailable(data.available || false);
      }
    } catch (error) {
      console.log('AI status check failed');
      setAiAvailable(false);
    }
  }, []);

  // Load user progress and preferences
  const loadUserProgress = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/user-progress/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setUserProgress(data);
        // Apply saved preferences
        if (data.chat_theme) setChatTheme(data.chat_theme);
        if (data.preferred_bot) setSelectedBot(data.preferred_bot);
        if (data.preferred_script) setScriptType(data.preferred_script);
        // Show welcome back if returning user
        if (data.total_conversations > 0) {
          setShowWelcomeBack(true);
          setTimeout(() => setShowWelcomeBack(false), 4000);
        }
      }
    } catch (error) {
      console.log('No saved progress found');
    }
  }, [userId]);

  // Save user preferences
  const saveUserPreferences = useCallback(async (prefs) => {
    if (!userId) return;
    try {
      await fetch(`${API_URL}/api/conversation-challenge/user-progress/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(prefs)
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  }, [userId]);

  // Search dictionary using AI (PyDODa + Groq)
  const searchAiDictionary = useCallback(async (word) => {
    if (!word || word.length < 2) return;
    
    setAiDictSearching(true);
    setAiDictResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/groq/search/${encodeURIComponent(word.trim())}`);
      if (response.ok) {
        const data = await response.json();
        if (!data.error) {
          // Transform AI result to match our dictionary format
          const aiWord = {
            searchedWord: word.trim(), // Save the searched word for display
            darija: data.data?.darija || data.word || word,
            english: data.data?.english || (Array.isArray(data.data?.meanings) ? data.data.meanings.join(', ') : (data.data?.meaning || '')),
            meaning: Array.isArray(data.data?.meanings) ? data.data.meanings.join(', ') : (data.data?.meaning || ''),
            phonetic: data.data?.pronunciation || data.data?.phonetic || '',
            arabic: data.data?.arabic || '',
            wordType: data.data?.type || data.data?.wordType || '',
            category: data.data?.category || '',
            grammarNote: data.data?.grammar_note || data.data?.grammarNote || '',
            conjugation: data.data?.conjugation || '',
            genderNote: data.data?.gender_note || data.data?.genderNote || '',
            examples: data.data?.examples || [],
            culturalNote: data.data?.cultural_note || data.data?.culturalNote || '',
            relatedWords: data.data?.related_words || data.data?.relatedWords || [],
            audioUrl: data.data?.audioUrl || data.data?.audio_url || '',
            source: data.source || 'ai', // 'curated', 'cache', 'groq_ai', 'pydoda'
            pydoda_verified: data.pydoda_verified || false
          };
          setAiDictResult(aiWord);
          setSelectedDictWord(aiWord);
        } else {
          console.log('AI dictionary error:', data.error);
        }
      }
    } catch (error) {
      console.error('Error searching AI dictionary:', error);
    } finally {
      setAiDictSearching(false);
    }
  }, []);

  // Extract all words from a bubble and look them up in dictionary
  const extractWordsFromBubble = useCallback(async (messageText) => {
    if (!messageText) return;

    // Split message into words, removing punctuation
    const words = messageText
      .split(/[\s.,!?،؟:;'"()-]+/)
      .map(w => w.trim().toLowerCase())
      .filter(w => w.length >= 2); // Filter out single characters

    // Remove duplicates
    const uniqueWords = [...new Set(words)];

    if (uniqueWords.length === 0) return;

    // Set loading state
    setDictWords([]);
    setSelectedDictWord(null);

    // First check flow vocabulary (instant, no API call needed)
    const flowVocab = selectedFlow?.vocabulary || [];
    const nodeVocab = selectedFlow?.nodes?.flatMap(n => n.data?.vocabulary || []) || [];
    const allFlowVocab = [...flowVocab, ...nodeVocab];

    const foundWords = [];
    const wordsNotInFlow = [];

    // Check each word against flow vocabulary first
    uniqueWords.forEach(word => {
      const vocabMatch = allFlowVocab.find(v =>
        v.darija?.toLowerCase() === word ||
        v.word?.toLowerCase() === word ||
        v.arabic === word ||
        v.wordArabic === word
      );
      if (vocabMatch) {
        foundWords.push({
          darija: vocabMatch.darija || vocabMatch.word || word,
          meaning: vocabMatch.meaning || vocabMatch.english || '',
          phonetic: vocabMatch.phonetic || vocabMatch.pronunciation || '',
          arabic: vocabMatch.arabic || '',
          wordType: vocabMatch.type || vocabMatch.wordType || '',
          source: 'flow',
          pydoda_verified: true
        });
      } else {
        wordsNotInFlow.push(word);
      }
    });

    // Only call API for words not found in flow vocabulary
    if (wordsNotInFlow.length > 0) {
      await Promise.all(wordsNotInFlow.map(async (word) => {
        try {
          const response = await fetch(`${API_URL}/api/conversation-challenge/groq/search/${encodeURIComponent(word)}`);
          if (response.ok) {
            const data = await response.json();
            if (!data.error && data.data) {
              foundWords.push({
                darija: data.data?.darija || word,
                meaning: Array.isArray(data.data?.meanings) ? data.data.meanings.join(', ') : (data.data?.meaning || data.data?.english || ''),
                phonetic: data.data?.pronunciation || data.data?.phonetic || '',
                arabic: data.data?.arabic || '',
                wordType: data.data?.type || data.data?.wordType || '',
                source: data.source || 'ai',
                pydoda_verified: data.pydoda_verified || false
              });
            }
          }
        } catch (error) {
          console.error(`Error looking up word: ${word}`, error);
        }
      }));
    }

    // Sort by original order in message
    foundWords.sort((a, b) => {
      const aIndex = uniqueWords.indexOf(a.darija.toLowerCase());
      const bIndex = uniqueWords.indexOf(b.darija.toLowerCase());
      return aIndex - bIndex;
    });

    setDictWords(foundWords);
    // Open mobile dictionary panel when words are found
    if (foundWords.length > 0) {
      setIsMobileDictOpen(true);
    }
  }, [selectedFlow]);

  // Save completed flow
  const saveFlowCompletion = useCallback(async (flowId, finalScore, learnedVocab) => {
    if (!userId) return;
    try {
      await fetch(`${API_URL}/api/conversation-challenge/user-progress/${userId}/complete-flow?flow_id=${flowId}&score=${finalScore}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(learnedVocab)
      });
      // Reload progress
      loadUserProgress();
    } catch (error) {
      console.error('Error saving completion:', error);
    }
  }, [userId, loadUserProgress]);

  useEffect(() => {
    loadBots();
    loadFlows();
    loadStories();
    loadUserProgress();
    loadIntents();
    loadTrainedConversations();
    checkAiStatus();
  }, [loadBots, loadFlows, loadStories, loadUserProgress, loadIntents, loadTrainedConversations, checkAiStatus]);

  const prevMsgCount = useRef(0);
  useEffect(() => {
    if (messages.length > prevMsgCount.current && chatContainerRef.current) {
      requestAnimationFrame(() => {
        chatContainerRef.current?.scrollTo({
          top: chatContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      });
    }
    prevMsgCount.current = messages.length;
  }, [messages.length]);
  
  useEffect(() => {
    if (waitingForInput && !showChoicesPanel && inputRef.current) {
      inputRef.current.focus();
    }
  }, [waitingForInput, showChoicesPanel]);

  // Memoize vocabulary outside message map to avoid recalculating per message per render
  const allVocabulary = useMemo(() => {
    const flowVocab = selectedFlow?.vocabulary || [];
    const nodeVocab = selectedFlow?.nodes?.flatMap(n => n.data?.vocabulary || []) || [];
    return [...flowVocab, ...nodeVocab];
  }, [selectedFlow]);

  // Stable callbacks for MessageBubble — prevent re-rendering all bubbles on every state change
  const handleWordClick = useCallback((word) => { setSelectedDictWord(word); setDictWords([]); }, []);
  const handleShowAllWords = useCallback((words) => { setDictWords(words); setSelectedDictWord(null); }, []);

  const filteredFlows = useMemo(() => {
    if (!selectedBot) return [];
    
    // STRICT FILTERING: Only match flows by bot_id
    // Same flows show in both Latin and Arabic modes - the script switch changes display only
    return flows.filter(flow => {
      // Only match if flow.bot_id exactly matches the selected bot's ID
      const botMatch = flow.bot_id === selectedBot;
      const activeMatch = flow.is_active !== false;
      const difficultyMatch = difficultyFilter === 'all' || flow.difficulty === difficultyFilter;
      return botMatch && activeMatch && difficultyMatch;
    });
  }, [flows, selectedBot, difficultyFilter]);

  // Get difficulty counts for the selected bot
  const difficultyCounts = useMemo(() => {
    if (!selectedBot) return { all: 0, Beginner: 0, Intermediate: 0, Advanced: 0 };
    const botFlows = flows.filter(flow => 
      flow.bot_id === selectedBot && 
      flow.is_active !== false
    );
    return {
      all: botFlows.length,
      Beginner: botFlows.filter(f => f.difficulty === 'Beginner').length,
      Intermediate: botFlows.filter(f => f.difficulty === 'Intermediate').length,
      Advanced: botFlows.filter(f => f.difficulty === 'Advanced').length
    };
  }, [flows, selectedBot, scriptType]);

  const delay = useCallback((ms) => new Promise(resolve => setTimeout(resolve, ms)), []);

  const playAudio = useCallback((url) => {
    if (!url) return;
    try {
      if (audioRef.current) audioRef.current.pause();
      audioRef.current = new Audio(url);
      audioRef.current.play();
    } catch (error) {
      console.error('Audio error:', error);
    }
  }, []);

  // Generate hint from answer text
  const generateHint = useCallback((answerText) => {
    if (!answerText) return 'Think about what would be natural to say here!';
    const words = answerText.split(' ');
    if (words.length <= 2) {
      return `The answer starts with "${words[0].charAt(0)}..." (${words.length} word${words.length > 1 ? 's' : ''})`;
    }
    // Show first word and hint about structure
    const firstWord = words[0];
    return `Start with "${firstWord}..." - the answer has ${words.length} words`;
  }, []);

  // Show hint handler
  const handleShowHint = useCallback(() => {
    setShowHint(true);
    setHintUsed(true);
  }, []);

  // Use counter to ensure unique message IDs (Date.now() can have duplicates when messages added quickly)
  const messageIdRef = useRef(0);
  const addMessage = useCallback((message) => {
    messageIdRef.current += 1;
    const uniqueId = `${Date.now()}_${messageIdRef.current}`;
    setMessages(prev => [...prev, { ...message, id: uniqueId, timestamp: new Date() }]);
  }, []);

  const getNextNodeFromEdges = useCallback((flow, nodeId) => {
    const edge = flow.edges?.find(e => e.source === nodeId);
    return edge?.target || null;
  }, []);

  const processNode = useCallback(async (flow, nodeId) => {
    if (!nodeId) { setConversationComplete(true); return; }
    const node = flow.nodes?.find(n => n.id === nodeId);
    if (!node) { setConversationComplete(true); return; }

    setCurrentNodeId(nodeId);
    const data = node.data || {};

    switch (node.type) {
      case 'message':
        // Instant message display - no typing indicator delay
        setIsTyping(false);
        // Store BOTH text versions so switching script works dynamically
        const latinText = data.text_darija || data.translation || '';
        const arabicText = data.text_arabic || '';
        // Pick a random GIF from gifUrls array, or fall back to single gifUrl
        const gifUrlsArray = (data.gifUrls && data.gifUrls.length > 0 ? data.gifUrls : (data.gifUrl ? [data.gifUrl] : [])).filter(u => u);
        const selectedGif = gifUrlsArray.length > 0 ? gifUrlsArray[Math.floor(Math.random() * gifUrlsArray.length)] : '';

        addMessage({
          type: 'bot',
          text: latinText,  // Default text (for backwards compatibility)
          text_latin: latinText,
          text_arabic: arabicText,
          translation: data.translation,
          audioUrl: data.audioUrl,
          imageUrl: data.imageUrl,
          gifUrl: selectedGif,
          emotion: data.emotion
        });
        if (data.audioUrl) playAudio(data.audioUrl);
        // Instant transition to next node - no delay
        await processNode(flow, data.nextNodeId || getNextNodeFromEdges(flow, nodeId));
        break;
      case 'response':
        setWaitingForInput(true);
        setWrongAttempts(0);
        setHintUsed(false);
        setShowHint(false);
        setTypingHintText(''); // Clear any previous hint
        // Generate hint from correct answer - store full answer for 40% hint
        const correctOption = data.options?.find(opt => opt.isCorrect);
        if (correctOption) {
          // Store the correct answer text for hint generation
          setCorrectAnswer(correctOption.text);
          const hintText = correctOption.hint || generateHint(correctOption.text);
          setCurrentHint(hintText);
        } else {
          setCorrectAnswer('');
          setCurrentHint('Try thinking about what would be polite in this situation!');
        }
        // If bot has is_smart_bot enabled, force text input mode (override per-node inputType)
        const isSmartBot = bots[selectedBot]?.is_smart_bot || false;
        const effectiveInputType = isSmartBot ? 'text' : (data.inputType || 'choice');
        if (effectiveInputType === 'choice') {
          console.log('📋 Setting choices from response node (choice mode):', data.options?.map(o => ({ text: o.text, isCorrect: o.isCorrect, intent: o.intent })));
          setShowChoices(true);
          setShowChoicesPanel(true);
          setChoices(data.options || []);
        }
        else {
          // For text input mode, still store options for Smart Bot intent matching
          // but don't show them as clickable choices
          console.log('📋 Setting choices from response node (text mode):', data.options?.map(o => ({ text: o.text, isCorrect: o.isCorrect, intent: o.intent })));
          setShowChoices(false);
          setChoices(data.options || []); // Keep options for Smart Bot matching!
        }
        break;
      case 'tip':
        setShowTip({ text: scriptType === 'arabic' && data.tipText_arabic ? data.tipText_arabic : data.tipText, type: data.tipType || 'info' });
        await delay(2000); // Display tip long enough to read
        setShowTip(null);
        await processNode(flow, data.nextNodeId || getNextNodeFromEdges(flow, nodeId));
        break;
      case 'vocabulary':
        // Show vocabulary info in the dictionary panel instead of popup
        if (data.wordDarija) {
          setSelectedDictWord({
            darija: data.wordDarija,
            meaning: data.translation,
            phonetic: data.pronunciation || ''
          });
        }
        // Auto-continue to next node - instant
        await processNode(flow, data.nextNodeId || getNextNodeFromEdges(flow, nodeId));
        break;
      case 'achievement':
        setScore(prev => prev + (data.points || 0));
        setShowAchievement({ name: data.achievementName, icon: data.achievementIcon || '🏆', points: data.points, message: data.message });
        // Non-blocking: auto-hide after 2s but continue flow immediately
        setTimeout(() => setShowAchievement(null), 2000);
        await processNode(flow, data.nextNodeId || getNextNodeFromEdges(flow, nodeId));
        break;
      case 'delay':
        // Skip delay nodes - instant navigation
        await processNode(flow, data.nextNodeId || getNextNodeFromEdges(flow, nodeId));
        break;
      case 'challenge':
        addMessage({ type: 'challenge', question: scriptType === 'arabic' && data.question_arabic ? data.question_arabic : data.question, question_arabic: data.question_arabic, challengeType: data.challengeType, options: data.options, points: data.points });
        setWaitingForInput(true);
        if (data.challengeType === 'choice') {
          setShowChoices(true);
          setShowChoicesPanel(true);
          setChoices(data.options?.map((opt, idx) => ({ id: `opt_${idx}`, text: opt, isCorrect: opt === data.correctAnswer, points: data.points || 10 })) || []);
        }
        break;
      case 'end':
        // Check if we're in a story with more chapters
        if (selectedStory && selectedStory.chapter_flows) {
          const currentChapterIdx = storyChapterIndex;
          const totalChapters = selectedStory.chapter_flows.length;
          
          if (currentChapterIdx < totalChapters - 1) {
            // More chapters available - show chapter complete message
            addMessage({ type: 'system', text: data.message || `✅ Chapter ${currentChapterIdx + 1} Complete!` });
            await delay(1500);
            
            const nextChapterIdx = currentChapterIdx + 1;
            addMessage({ type: 'system', text: `📖 Starting Chapter ${nextChapterIdx + 1}...` });
            await delay(1000);
            
            // Update chapter index
            setStoryChapterIndex(nextChapterIdx);
            
            // Get and start next chapter
            const nextChapter = selectedStory.chapter_flows[nextChapterIdx];
            if (nextChapter) {
              // Reset state for new chapter
              setSelectedFlow(nextChapter);
              setCurrentNodeId(null);
              setWaitingForInput(false);
              setShowChoices(false);
              setChoices([]);
              setShowHint(false);
              setHintUsed(false);
              setConversationComplete(false);
              
              // Find and process the start node of next chapter
              const startNode = nextChapter.nodes?.find(n => n.type === 'start');
              if (startNode) {
                const firstEdge = nextChapter.edges?.find(e => e.source === startNode.id);
                if (firstEdge) {
                  await processNode(nextChapter, firstEdge.target);
                }
              }
            }
          } else {
            // Last chapter - story complete!
            setConversationComplete(true);
            setSummary(data.summary || []);
            addMessage({ type: 'system', text: '🎉 Story Complete! You finished all chapters!' });
            saveFlowCompletion(flow._id || flow.id, score, dictWords);
            setSelectedStory(null); // Clear story
          }
        } else {
          // Not in a story - normal conversation end
          setConversationComplete(true);
          setSummary(data.summary || []);
          addMessage({ type: 'system', text: data.message || '🎉 Conversation complete!' });
          saveFlowCompletion(flow._id || flow.id, score, dictWords);
        }
        break;
      default:
        console.warn(`⚠️ Unknown node type: "${node_type}", skipping to next node`);
        const defaultNext = getNextNodeFromEdges(flow, nodeId);
        if (defaultNext) await processNode(flow, defaultNext);
    }
  }, [scriptType, delay, addMessage, playAudio, getNextNodeFromEdges, prefersReducedMotion, selectedStory, storyChapterIndex, bots, selectedBot, saveFlowCompletion, score, dictWords]);

  const startConversation = useCallback(async (flow) => {
    if (!flow.is_free && !hasPaidPlan) {
      showNotification?.('🔒 This conversation requires PRO membership', 'error');
      return;
    }
    setLoading(true);
    setSelectedFlow(flow);
    setMessages([]);
    setScore(0);
    setStreak(0);
    setConversationComplete(false);
    setSummary([]);
    // Clear dictionary panel on conversation start
    setSelectedDictWord(null);
    setDictWords([]);
    setDictSearchQuery('');
    setView('chat');
    const startNode = flow.nodes?.find(n => n.type === 'start');
    if (startNode) {
      const firstEdge = flow.edges?.find(e => e.source === startNode.id);
      if (firstEdge) await processNode(flow, firstEdge.target);
    }
    setLoading(false);
  }, [hasPaidPlan, showNotification, processNode]);

  // Start a story (multi-chapter conversation)
  const startStory = useCallback(async (story) => {
    console.log('Starting story:', story.id, story.name);
    
    if (!story.is_free && !hasPaidPlan) {
      showNotification?.('🔒 This story requires PRO membership', 'error');
      return;
    }
    
    // Load the story with its chapters (flows)
    try {
      console.log('Fetching story from API...');
      const response = await fetch(`${API_URL}/api/conversation-challenge/stories/${story.id}`);
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const fullStory = await response.json();
        console.log('Full story loaded:', fullStory.name, 'chapters:', fullStory.chapter_flows?.length);
        
        const chapters = fullStory.chapter_flows || [];
        
        if (chapters.length === 0) {
          showNotification?.('📚 This story has no chapters yet!', 'info');
          return;
        }
        
        setSelectedStory(fullStory);
        setStoryChapterIndex(0);
        
        // Start with first chapter
        const firstChapter = chapters[0];
        console.log('Starting first chapter:', firstChapter?.name);
        
        if (firstChapter) {
          await startConversation(firstChapter);
        }
      } else {
        console.error('Failed to load story:', response.status);
        showNotification?.('Failed to load story', 'error');
      }
    } catch (error) {
      console.error('Error loading story:', error);
      showNotification?.('Error loading story: ' + error.message, 'error');
    }
  }, [hasPaidPlan, showNotification, startConversation]);

  // Go to next chapter in story
  const nextChapter = useCallback(async () => {
    if (!selectedStory || !selectedStory.chapter_flows) return;
    
    const nextIndex = storyChapterIndex + 1;
    if (nextIndex < selectedStory.chapter_flows.length) {
      setStoryChapterIndex(nextIndex);
      const nextFlow = selectedStory.chapter_flows[nextIndex];
      if (nextFlow) {
        await startConversation(nextFlow);
      }
    } else {
      // Story complete
      showNotification?.('🎉 Story complete! You finished all chapters!', 'success');
      const lastFlow = selectedStory.chapter_flows[selectedStory.chapter_flows.length - 1];
      if (lastFlow) saveFlowCompletion(lastFlow._id || lastFlow.id, score, dictWords);
      setSelectedStory(null);
      setView('bot-select');
    }
  }, [selectedStory, storyChapterIndex, startConversation, showNotification, saveFlowCompletion, score, dictWords]);

  // ============================================
  // INTENT RECOGNITION SYSTEM
  // Bot understands what user MEANS, not just exact words
  // Intents are loaded from API and can be edited in admin
  // ============================================

  // Get active intents (from API or fallback to defaults)
  const getActiveIntents = useCallback(() => {
    // If intents loaded from API, use them
    if (Object.keys(intents).length > 0) {
      return intents;
    }
    // Fallback to basic defaults if API not loaded yet
    return {
      greeting: ['salam', 'marhba', 'hello', 'hi', 'sba7 lkhir'],
      goodbye: ['bslama', 'bye', 'goodbye'],
      thanks: ['shukran', 'merci', 'thanks'],
      yes: ['iyeh', 'wakha', 'yes', 'ok'],
      no: ['la', 'no', 'non']
    };
  }, [intents]);

  // Detect user intent from their message
  const detectIntent = useCallback((text) => {
    const normalizedText = text.toLowerCase().trim()
      .replace(/[.,!?،؟]/g, '') // Remove punctuation
      .replace(/\s+/g, ' '); // Normalize spaces

    const activeIntents = getActiveIntents();

    for (const [intent, phrases] of Object.entries(activeIntents)) {
      for (const phrase of phrases) {
        // Word-boundary matching only (prevents "la" matching inside "labas")
        if (normalizedText === phrase ||
            normalizedText.startsWith(phrase + ' ') ||
            normalizedText.endsWith(' ' + phrase) ||
            normalizedText.includes(' ' + phrase + ' ')) {
          return intent;
        }
      }
    }
    return null;
  }, [getActiveIntents]);

  // Returns ALL matching intents for a text (for ambiguity check)
  const detectAllIntents = useCallback((text) => {
    const normalizedText = text.toLowerCase().trim()
      .replace(/[.,!?،؟]/g, '').replace(/\s+/g, ' ');
    const activeIntents = getActiveIntents();
    const matched = [];
    for (const [intent, phrases] of Object.entries(activeIntents)) {
      for (const phrase of phrases) {
        if (normalizedText === phrase ||
            normalizedText.startsWith(phrase + ' ') ||
            normalizedText.endsWith(' ' + phrase) ||
            normalizedText.includes(' ' + phrase + ' ')) {
          matched.push(intent);
          break;
        }
      }
    }
    return matched;
  }, [getActiveIntents]);

  // Check if an option matches an intent
  const optionMatchesIntent = useCallback((optionText, intent) => {
    if (!optionText || !intent) return false;
    const normalizedOpt = optionText.toLowerCase().replace(/[.,!?،؟]/g, '');
    const activeIntents = getActiveIntents();
    const intentPhrases = activeIntents[intent] || [];

    return intentPhrases.some(phrase =>
      normalizedOpt.includes(phrase) || phrase.includes(normalizedOpt.split(' ')[0])
    );
  }, [getActiveIntents]);

  // Get bot response for a detected intent (for smart intent pause feature)
  const getIntentResponse = useCallback((intent) => {
    const responses = {
      greeting: {
        botResponse: "Salam! Labas? 😊",
        botResponseArabic: "سلام! لاباس؟",
        translation: "Hello! How are you?"
      },
      goodbye: {
        botResponse: "Bslama! Ttsenna, bghiti tkemmel?",
        botResponseArabic: "بسلامة! تسنى، بغيتي تكمل؟",
        translation: "Goodbye! Wait, do you want to continue?"
      },
      thanks: {
        botResponse: "3afak! Marhba bik 😊",
        botResponseArabic: "عفاك! مرحبا بيك",
        translation: "You're welcome!"
      },
      fine: {
        botResponse: "Hamdullah! Mezyan!",
        botResponseArabic: "الحمد لله! مزيان!",
        translation: "Thank God! Good!"
      },
      yes: {
        botResponse: "Wakha, yallah nkemmlo!",
        botResponseArabic: "واخا، يالله نكملو!",
        translation: "Okay, let's continue!"
      },
      no: {
        botResponse: "Wakha, bslama!",
        botResponseArabic: "واخا، بسلامة!",
        translation: "Okay, goodbye!"
      }
    };
    return responses[intent] || null;
  }, []);

  // Handle smart intent pause - user said something off-topic, bot responds and asks to continue
  const handleSmartIntentPause = useCallback((userText, detectedIntent) => {
    const intentResponse = getIntentResponse(detectedIntent);
    if (!intentResponse) return false;

    // Show user's message
    addMessage({ type: 'user', text: userText });

    // Bot responds to the intent
    setTimeout(() => {
      addMessage({
        type: 'bot',
        text: intentResponse.botResponse,
        translation: intentResponse.translation
      });

      // After responding, ask if they want to continue (except for 'yes' and 'no' which are answers)
      if (detectedIntent !== 'yes' && detectedIntent !== 'no') {
        setTimeout(() => {
          addMessage({
            type: 'bot',
            text: "Bghiti tkemmel t3elem? 📚",
            translation: "Do you want to continue learning?"
          });

          // Store current node and activate pause mode
          setPausedNodeId(currentNodeId);
          setIntentPauseActive(true);

          // Show Yes/No choices - MUST set showChoicesPanel to true for them to be visible
          setChoices([
            { id: 'continue_yes', text: 'Iyeh! (Yes)', isCorrect: true, isContinueChoice: true },
            { id: 'continue_no', text: 'La, shukran (No)', isCorrect: false, isContinueChoice: true }
          ]);
          setShowChoices(true);
          setShowChoicesPanel(true); // This shows the choices panel!
          setWaitingForInput(true);
        }, 1000);
      } else if (detectedIntent === 'yes' && intentPauseActive) {
        // User said yes to continue - resume flow
        setIntentPauseActive(false);
        // Re-show the current question
        setTimeout(() => {
          const currentNode = selectedFlow?.nodes?.find(n => n.id === pausedNodeId);
          if (currentNode?.data?.prompt) {
            addMessage({
              type: 'bot',
              text: currentNode.data.prompt,
              translation: currentNode.data.promptTranslation || ''
            });
          }
          // Restore original choices
          setChoices(currentNode?.data?.options || []);
          setShowChoices(currentNode?.data?.inputType === 'choice');
          setWaitingForInput(true);
        }, 500);
      } else if (detectedIntent === 'no' && intentPauseActive) {
        // User said no - end conversation
        setIntentPauseActive(false);
        setTimeout(() => {
          setConversationComplete(true);
        }, 500);
      }
    }, 500);

    setUserInput('');
    return true; // Handled
  }, [addMessage, getIntentResponse, currentNodeId, intentPauseActive, pausedNodeId, selectedFlow]);

  // Smart pattern matching for flexible responses
  const smartMatchResponse = useCallback((userText, options) => {
    const normalizedInput = userText.toLowerCase().trim();

    // First try exact match
    let match = options?.find(opt => opt.text?.toLowerCase().trim() === normalizedInput);
    if (match) return match;

    // Detect user's intent
    const userIntent = detectIntent(normalizedInput);
    console.log('Detected intent:', userIntent, 'from:', normalizedInput);

    if (userIntent) {
      console.log('🔍 User intent detected:', userIntent);
      console.log('📋 Options to check:', options?.map(o => ({ text: o.text?.substring(0, 30), isCorrect: o.isCorrect, intent: o.intent })));

      // STRICT INTENT MATCHING: Only match if the correct option has an EXPLICIT intent tag
      // This prevents "hello" from matching "Labas, hamdullah!" unless that option is tagged with intent: "greeting"
      const correctOption = options?.find(opt => opt.isCorrect);
      if (correctOption && correctOption.intent === userIntent) {
        console.log('🎯 Intent match: correct option has explicit intent tag:', correctOption.intent);
        return { ...correctOption, smartMatched: true, detectedIntent: userIntent };
      }

      // For introduction intent, accept if pattern matches (special case for name introductions)
      if (userIntent === 'introduction') {
        if (/^ana\s+(mn|men|min|smiti|smiyti)\s+.+/i.test(normalizedInput) ||
            /^(jiit|jay)\s+(mn|men)\s+.+/i.test(normalizedInput)) {
          if (correctOption && correctOption.intent === 'introduction') {
            return { ...correctOption, smartMatched: true, detectedIntent: userIntent };
          }
        }
      }

      // Auto-infer intent: if correct option has NO explicit tag, check if its text
      // matches the SAME intent as the user's input (e.g., "Sbah l'khir!" = greeting, "hello" = greeting)
      // Safety: only auto-infer if the correct answer matches exactly ONE intent (not ambiguous)
      if (correctOption && !correctOption.intent) {
        const correctTextIntent = detectIntent(correctOption.text || '');
        if (correctTextIntent && correctTextIntent === userIntent) {
          const allIntents = detectAllIntents(correctOption.text || '');
          if (allIntents.length === 1) {
            console.log('🧠 Auto-inferred intent match:', userIntent, '(unambiguous, correct answer matches only this intent)');
            return { ...correctOption, smartMatched: true, detectedIntent: userIntent };
          } else {
            console.log('⚠️ Skipping auto-infer: correct answer matches multiple intents:', allIntents);
          }
        }
      }

      console.log('❌ No intent match - correct option does not have matching intent tag');
    }

    // Partial match - check if input contains key parts of an option
    // Require at least 3 characters match and higher threshold
    match = options?.find(opt => {
      const optText = opt.text?.toLowerCase() || '';
      const optWords = optText.split(/\s+/).filter(w => w.length >= 3);
      const inputWords = normalizedInput.split(/\s+/).filter(w => w.length >= 3);

      // Skip if input is too short or too different in length
      if (inputWords.length === 0 || optWords.length === 0) return false;
      if (Math.abs(inputWords.length - optWords.length) > 3) return false;

      const matchCount = optWords.filter(w =>
        inputWords.some(iw => iw === w || (iw.length >= 4 && w.includes(iw)) || (w.length >= 4 && iw.includes(w)))
      ).length;

      // Require at least 60% match AND at least 2 words matching
      return matchCount >= 2 && matchCount >= Math.ceil(optWords.length * 0.6);
    });
    if (match) return match;

    // Fuzzy match — Darija spelling tolerance (client-side)
    if (fuzzySettings?.enabled) {
      const correctOpts = options?.filter(opt => opt.isCorrect) || [];
      if (correctOpts.length > 0) {
        const eqs = fuzzySettings.equivalences || [];
        const threshold = fuzzySettings.threshold || 75;

        // Apply equivalences with longest-match-first + collapse doubles
        const applyEqs = (text) => {
          let r = text.toLowerCase().trim().replace(/[.,!?;:،؟\-'"()]+/g, '');
          const sorted = [...eqs].filter(e => e.from && e.to).sort((a, b) => b.from.length - a.from.length);
          let i = 0, out = [];
          while (i < r.length) {
            let matched = false;
            for (const eq of sorted) {
              if (r.substring(i, i + eq.from.length) === eq.from) {
                out.push(eq.to); i += eq.from.length; matched = true; break;
              }
            }
            if (!matched) { out.push(r[i]); i++; }
          }
          r = out.join('');
          r = r.replace(/(.)\1+/g, '$1'); // collapse doubles
          if (r.length > 3 && r.endsWith('e')) r = r.slice(0, -1); // strip trailing e
          return r;
        };

        // Levenshtein distance
        const levDist = (a, b) => {
          if (a.length < b.length) return levDist(b, a);
          if (b.length === 0) return a.length;
          let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
          for (let i = 0; i < a.length; i++) {
            const curr = [i + 1];
            for (let j = 0; j < b.length; j++) {
              curr.push(Math.min(curr[j] + 1, prev[j + 1] + 1, prev[j] + (a[i] === b[j] ? 0 : 1)));
            }
            prev = curr;
          }
          return prev[b.length];
        };

        let bestScore = 0;
        let bestOpt = null;
        const userNorm = applyEqs(normalizedInput);

        for (const opt of correctOpts) {
          const optNorm = applyEqs(opt.text || '');
          if (!optNorm) continue;
          if (userNorm === optNorm) { bestScore = 100; bestOpt = opt; break; }
          // Word set match
          const uw = new Set(userNorm.split(/\s+/));
          const ow = new Set(optNorm.split(/\s+/));
          if (uw.size > 0 && ow.size > 0 && [...uw].every(w => ow.has(w)) && [...ow].every(w => uw.has(w))) {
            bestScore = 100; bestOpt = opt; break;
          }
          const maxLen = Math.max(userNorm.length, optNorm.length);
          const score = maxLen > 0 ? Math.round((1 - levDist(userNorm, optNorm) / maxLen) * 100) : 0;
          if (score > bestScore) { bestScore = score; bestOpt = opt; }
        }

        if (bestScore >= threshold && bestOpt) {
          console.log(`🔤 Fuzzy match: "${normalizedInput}" ≈ "${bestOpt.text}" (${bestScore}%)`);
          return { ...bestOpt, smartMatched: true, fuzzyMatched: true, fuzzyScore: bestScore };
        }
      }
    }

    return null;
  }, [detectIntent, detectAllIntents, optionMatchesIntent, fuzzySettings]);

  const handleUserResponse = useCallback(async (response, isCorrect = null, points = 0) => {
    if (!selectedFlow) return;

    // Handle both old format (string) and new format ({ userText, matchedOption })
    let displayText, matchedOption;
    if (typeof response === 'object' && response.userText) {
      // New format: show user's actual input, use matchedOption for validation
      displayText = response.userText;
      matchedOption = response.matchedOption;
    } else {
      // Old format (from clicking choices): show the choice text
      displayText = typeof response === 'string' ? response : response.text;
      matchedOption = null;
    }

    addMessage({ type: 'user', text: displayText });
    setWaitingForInput(false);
    setShowChoices(false);
    setChoices([]);
    setUserInput('');
    // Clear hints when user responds
    setShowHint(false);
    setTypingHintText('');
    setShowChoicesPanel(false);
    if (isCorrect !== null) {
      if (isCorrect) { setScore(prev => prev + points); setStreak(prev => prev + 1); }
      else { setStreak(0); }
    }
    const currentNode = selectedFlow.nodes?.find(n => n.id === currentNodeId);
    if (!currentNode) return;
    const data = currentNode.data || {};
    let nextNodeId = null;
    if (currentNode.type === 'response') {
      // Use pre-matched option if available (from typed input), otherwise match
      const option = matchedOption || smartMatchResponse(displayText, data.options);
      if (option) {
        // Use option's nextNodeId, or fall back to data.nextNodeId, or use edges
        nextNodeId = option.nextNodeId || data.nextNodeId || getNextNodeFromEdges(selectedFlow, currentNodeId);
        if (option.isCorrect) {
          if (option.smartMatched && option.detectedIntent) {
            // Show intent-specific feedback
            const intentFeedback = {
              greeting: '✅ Mezyan! Great greeting!',
              goodbye: '✅ Bslama! Nice goodbye!',
              thanks: '✅ 3afak! Good manners!',
              fine: '✅ Hamdullah! Great response!',
              yes: '✅ Wakha! Understood!',
              agreement: '✅ Safi! Got it!',
              introduction: '✅ Mtshrfin! Nice to meet you!',
              ordering: '✅ Wakha! Good order!',
              please: '✅ Very polite!',
              apology: '✅ Makayn moshkil! No problem!'
            };
            addMessage({ type: 'system', text: intentFeedback[option.detectedIntent] || '✅ Mezyan! (Good answer!)' });
          } else if (option.fuzzyMatched) {
            const tip = fuzzySettings?.show_tip && fuzzySettings?.tip_template
              ? fuzzySettings.tip_template.replace('{answer}', option.text)
              : '';
            addMessage({ type: 'system', text: `✅ Mezyan!${tip ? ' ' + tip : ''}` });
          } else if (option.smartMatched) {
            addMessage({ type: 'system', text: '✅ Mezyan! (Good answer!)' });
          } else {
            addMessage({ type: 'system', text: '✅ Correct!' });
          }
        } else if (data.wrongPathNodeId) {
          nextNodeId = data.wrongPathNodeId;
        }
      } else if (data.acceptAny) {
        nextNodeId = data.nextNodeId || getNextNodeFromEdges(selectedFlow, currentNodeId);
      } else {
        nextNodeId = data.wrongPathNodeId || data.nextNodeId;
      }
    } else if (currentNode.type === 'challenge') {
      const userAns = (typeof response === 'string' ? response : response?.text || '').toLowerCase().trim();
      const correctAns = (data.correctAnswer || '').toLowerCase().trim();
      let correct = userAns === correctAns;

      // Fuzzy match fallback for challenge answers
      if (!correct && fuzzySettings?.enabled && userAns && correctAns) {
        const eqs = fuzzySettings.equivalences || [];
        const applyEqs = (text) => {
          let r = text.replace(/[.,!?;:،؟\-'"()]+/g, '');
          const sorted = [...eqs].filter(e => e.from && e.to).sort((a, b) => b.from.length - a.from.length);
          let i = 0, out = [];
          while (i < r.length) {
            let matched = false;
            for (const eq of sorted) {
              if (r.substring(i, i + eq.from.length) === eq.from) {
                out.push(eq.to); i += eq.from.length; matched = true; break;
              }
            }
            if (!matched) { out.push(r[i]); i++; }
          }
          r = out.join('').replace(/(.)\1+/g, '$1');
          if (r.length > 3 && r.endsWith('e')) r = r.slice(0, -1);
          return r;
        };
        const levDist = (a, b) => {
          if (a.length < b.length) return levDist(b, a);
          if (b.length === 0) return a.length;
          let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
          for (let i = 0; i < a.length; i++) {
            const curr = [i + 1];
            for (let j = 0; j < b.length; j++) {
              curr.push(Math.min(curr[j] + 1, prev[j + 1] + 1, prev[j] + (a[i] === b[j] ? 0 : 1)));
            }
            prev = curr;
          }
          return prev[b.length];
        };
        const un = applyEqs(userAns), cn = applyEqs(correctAns);
        const maxLen = Math.max(un.length, cn.length);
        const score = maxLen > 0 ? Math.round((1 - levDist(un, cn) / maxLen) * 100) : 0;
        if (score >= (fuzzySettings.threshold || 75)) {
          correct = true;
        }
      }

      if (correct) {
        const tip = fuzzySettings?.show_tip && userAns !== correctAns && fuzzySettings?.tip_template
          ? ' ' + fuzzySettings.tip_template.replace('{answer}', data.correctAnswer) : '';
        nextNodeId = data.correctPathNodeId;
        addMessage({ type: 'system', text: `✅ Correct! +${data.points || 10} points${tip}` });
      } else {
        nextNodeId = data.wrongPathNodeId;
        addMessage({ type: 'system', text: `❌ Not quite. The answer was: ${data.correctAnswer}` });
      }
    }
    // Show typing indicator before bot responds
    // Skip typing delay for smart intent matches - user already got instant feedback
    if (matchedOption?.smartMatched) {
      // Smart match: skip typing delay for snappy response
      await processNode(selectedFlow, nextNodeId);
    } else {
      setIsTyping(true);
      await delay(prefersReducedMotion ? 400 : 800); // Simulate bot thinking
      setIsTyping(false);
      await processNode(selectedFlow, nextNodeId);
    }
  }, [selectedFlow, currentNodeId, addMessage, getNextNodeFromEdges, delay, processNode, prefersReducedMotion, smartMatchResponse]);

  // ============================================
  // TRAINED CONVERSATION PLAY ENGINE
  // Much simpler than flow processNode — just walk through steps
  // ============================================
  // Process a trained step by type — handles all node types
  // Non-exchange/quiz types auto-advance to next step
  const processTrainedStep = useCallback((conv, index, currentScore) => {
    // Bounds checking — validate steps array exists and index is valid
    if (!conv?.steps || !Array.isArray(conv.steps) || conv.steps.length === 0) {
      addMessage({ type: 'system', text: 'This conversation has no steps. Please try another one.' });
      setConversationComplete(true);
      setWaitingForInput(false);
      return;
    }
    if (index < 0 || index >= conv.steps.length) {
      // Out of bounds — gracefully end conversation with summary
      addMessage({ type: 'system', text: `🎉 Conversation complete! You scored ${currentScore} points!` });
      setConversationComplete(true);
      setWaitingForInput(false);
      saveFlowCompletion(conv._id, currentScore, []);
      return;
    }
    const step = conv.steps[index];
    if (!step) {
      // Null/undefined step — skip to next
      addMessage({ type: 'system', text: '⚠️ Skipping broken step...' });
      setTimeout(() => processTrainedStep(conv, index + 1, currentScore), 500);
      return;
    }
    setCurrentStepIndex(index);
    setStepAttempts(0); // Reset attempt counter for new step
    const stepType = step.type || 'exchange';

    // Gender-aware text helper
    const gText = (base, male) => (userGender === 'male' && male != null && male !== '') ? male : base;

    switch (stepType) {
      case 'exchange': {
        const latinText = gText(step.bot_message, step.bot_message_male);
        const arabicText = gText(step.bot_message_arabic, step.bot_message_arabic_male);
        if (!latinText && !arabicText) {
          addMessage({ type: 'system', text: '⚠️ Skipping step with missing content...' });
          setTimeout(() => processTrainedStep(conv, index + 1, currentScore), 500);
          break;
        }
        const botAudio = gText(step.audioUrl, step.audioUrl_male);
        const selectedGif = step.gifUrls?.length > 0 ? step.gifUrls[Math.floor(Math.random() * step.gifUrls.length)] : step.gifUrl;
        // Show typing dots first, then reveal message
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          addMessage({
            type: 'bot', text: latinText || arabicText, text_arabic: arabicText || undefined, translation: step.bot_translation || '',
            imageUrl: step.imageUrl || undefined, gifUrl: selectedGif || undefined, audioUrl: botAudio || undefined
          });
          if (botAudio) playAudio(botAudio);
          setWaitingForInput(true);
          if (step.choices?.length > 0) {
            setTrainedResponseOptions({ options: step.choices, stepIndex: index, conv });
          }
        }, 800);
        break;
      }
      case 'message': {
        const latinText = gText(step.text, step.text_male);
        const arabicText = gText(step.text_arabic, step.text_arabic_male);
        const msgGif = step.gifUrls?.length > 0 ? step.gifUrls[Math.floor(Math.random() * step.gifUrls.length)] : step.gifUrl;
        // Show typing dots first, then reveal message
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          addMessage({ type: 'bot', text: latinText || '', text_arabic: arabicText || undefined, translation: step.translation || '', gifUrl: msgGif, imageUrl: step.imageUrl });
          if (step.audioUrl) playAudio(step.audioUrl);
          setTimeout(() => processTrainedStep(conv, index + 1, currentScore), 700);
        }, 800);
        break;
      }
      case 'achievement': {
        const achMsg = gText(step.message, step.message_male);
        setShowAchievement({ name: step.name, icon: step.icon || '🏆', points: step.points || 0, message: achMsg });
        setScore(prev => prev + (step.points || 0));
        setTimeout(() => {
          setShowAchievement(null);
          processTrainedStep(conv, index + 1, currentScore + (step.points || 0));
        }, 2000);
        break;
      }
      case 'tip': {
        const tipText = gText(step.text, step.text_male);
        setShowTip({ text: tipText, type: step.tipType || 'info' });
        if (step.audioUrl) playAudio(step.audioUrl);
        setTimeout(() => { setShowTip(null); processTrainedStep(conv, index + 1, currentScore); }, 2500);
        break;
      }
      case 'vocabulary': {
        setSelectedDictWord({ darija: step.darija, meaning: step.english, phonetic: step.phonetic || '' });
        addMessage({ type: 'bot', text: `📚 New word: ${step.darija} — ${step.english}` });
        setTimeout(() => processTrainedStep(conv, index + 1, currentScore), 2000);
        break;
      }
      case 'audio': {
        if (step.audioUrl) playAudio(step.audioUrl);
        addMessage({ type: 'bot', text: step.label || '🔊 Listen...' });
        setTimeout(() => processTrainedStep(conv, index + 1, currentScore), 2000);
        break;
      }
      case 'response': {
        const prompt = step.prompt || 'What do you say?';
        const promptArabic = step.prompt_arabic || '';
        addMessage({ type: 'bot', text: `❓ ${prompt}`, text_arabic: promptArabic ? `❓ ${promptArabic}` : undefined });
        if (step.inputType === 'choice' && step.options?.length > 0) {
          setTrainedResponseOptions({ options: step.options, stepIndex: index, conv });
        }
        setWaitingForInput(true);
        break;
      }
      case 'quiz': {
        const qLatin = gText(step.question, step.question_male);
        const qArabic = gText(step.question_arabic, step.question_arabic_male);
        addMessage({ type: 'bot', text: `🎯 ${qLatin}`, text_arabic: qArabic ? `🎯 ${qArabic}` : undefined });
        setWaitingForInput(true);
        break;
      }
      case 'branch': {
        console.log('[BRANCH] Step', index, 'choices:', step.choices?.length, 'branches:', step.branches?.length, 'step:', JSON.stringify(step).slice(0,200));
        const branchLatin = gText(step.bot_message, step.bot_message_male);
        const branchArabic = gText(step.bot_message_arabic, step.bot_message_arabic_male);
        const branchText = branchLatin || branchArabic || step.prompt || '';
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          if (branchText) {
            addMessage({ type: 'bot', text: branchText, text_arabic: branchArabic || undefined, translation: step.bot_translation || '' });
          }
          setWaitingForInput(true);
          // Show branch choices as clickable buttons
          if (step.choices?.length > 0) {
            const branchOptions = step.choices.map(c => ({
              text: c.text,
              isCorrect: true,
              points: 10,
              _branchKeywords: c.keywords
            }));
            setTrainedResponseOptions({ options: branchOptions, stepIndex: index, conv, isBranch: true });
          } else if (step.branches?.length > 0) {
            // Fallback: generate buttons from branches
            const branchOptions = step.branches.map(b => ({
              text: (b.keywords || [])[0] || b.id,
              isCorrect: true,
              points: b.points || 10,
              _branchKeywords: b.keywords
            }));
            setTrainedResponseOptions({ options: branchOptions, stepIndex: index, conv, isBranch: true });
          }
        }, 800);
        break;
      }
      case 'delay': {
        const delayTime = step.delayMs || 1500;
        if (step.showTyping) addMessage({ type: 'system', text: '...' });
        setTimeout(() => processTrainedStep(conv, index + 1, currentScore), delayTime);
        break;
      }
      case 'end': {
        if (step.message) addMessage({ type: 'system', text: step.message });
        if (step.summary?.length > 0) {
          addMessage({ type: 'system', text: '📝 Words learned:\n' + step.summary.join('\n') });
        }
        addMessage({ type: 'system', text: '🎉 Conversation complete! Great job!' });
        setConversationComplete(true);
        setWaitingForInput(false);
        saveFlowCompletion(conv._id, currentScore, []);
        break;
      }
      default: {
        const msg = step.bot_message || step.text || '';
        if (msg) addMessage({ type: 'bot', text: msg });
        setWaitingForInput(true);
      }
    }
  }, [userGender, addMessage, playAudio, setShowAchievement, setShowTip, setSelectedDictWord, saveFlowCompletion, setStepAttempts]);

  // Handle listen prompt choice (Yes = play audio, Skip = advance)
  const handleListenPromptChoice = useCallback((choice) => {
    if (!listenPromptData) return;
    const { audioUrl, conv, nextIdx, nextScore } = listenPromptData;
    setListenPromptData(null);
    if (choice === 'yes' && audioUrl) {
      playAudio(audioUrl);
      addMessage({ type: 'system', text: '🔊 Listen carefully...' });
      setTimeout(() => processTrainedStep(conv, nextIdx, nextScore), 2500);
    } else {
      processTrainedStep(conv, nextIdx, nextScore);
    }
  }, [listenPromptData, playAudio, addMessage, processTrainedStep]);

  const startTrainedConversation = useCallback((conv) => {
    setTrainedMode(true);
    setCurrentTrainedConv(conv);
    setCurrentStepIndex(0);
    setMessages([]);
    setScore(0);
    setConversationComplete(false);
    setView('chat');
    setWaitingForInput(false);
    setListenPromptData(null);
    // Use the bot assigned to this conversation
    if (conv.bot_id && conv.bot_id !== 'default') {
      setSelectedBot(conv.bot_id);
    }
    // Handle gender_mode: 'ask' = show gender picker, 'neutral' = skip it
    const genderMode = conv.gender_mode || 'neutral';
    if (genderMode === 'ask') {
      // Show gender picker
      setTrainedGenderPending(conv);
      setTimeout(() => {
        addMessage({ type: 'bot', text: 'Who are you speaking to? Darija uses different forms for male and female.', noRtl: true });
      }, 300);
    } else {
      // Skip gender prompt — use female/default
      setUserGender('female');
      setTrainedGenderPending(null);
      setTimeout(() => processTrainedStep(conv, 0, 0), 300);
    }
  }, [addMessage, processTrainedStep]);

  const handleGenderSelect = useCallback((gender) => {
    if (!trainedGenderPending) return;
    setUserGender(gender);
    addMessage({ type: 'user', text: gender === 'female' ? '♀ Female' : '♂ Male' });
    const conv = trainedGenderPending;
    setTrainedGenderPending(null);
    setTimeout(() => processTrainedStep(conv, 0, 0), 500);
  }, [trainedGenderPending, addMessage, processTrainedStep]);

  // Handle response option selection (from Response node or Branch node)
  const handleResponseOption = useCallback((option) => {
    if (!trainedResponseOptions) return;
    const { conv, stepIndex, isBranch } = trainedResponseOptions;
    addMessage({ type: 'user', text: option.text });
    setTrainedResponseOptions(null);
    setWaitingForInput(false);

    // Branch routing: find matching branch by keywords and jump to next_step_id
    if (isBranch) {
      const step = conv.steps[stepIndex];
      const branches = step?.branches || [];
      const clickedKeywords = option._branchKeywords || [];
      let matchedBranch = null;
      for (const branch of branches) {
        const branchKws = branch.keywords || [];
        if (clickedKeywords.some(kw => branchKws.includes(kw))) {
          matchedBranch = branch;
          break;
        }
      }
      if (matchedBranch) {
        const pts = matchedBranch.points || 10;
        setScore(prev => prev + pts);
        if (matchedBranch.response) {
          addMessage({ type: 'system', text: `✅ ${matchedBranch.response}` });
        }
        let nextIdx = stepIndex + 1;
        if (matchedBranch.next_step_id) {
          const foundIdx = conv.steps.findIndex(s => s.id === matchedBranch.next_step_id);
          if (foundIdx >= 0) nextIdx = foundIdx;
        }
        setTimeout(() => processTrainedStep(conv, nextIdx, score + pts), 800);
      } else {
        // Fallback: just advance linearly
        setTimeout(() => processTrainedStep(conv, stepIndex + 1, score), 800);
      }
      return;
    }

    // Normal response option
    if (option.isCorrect) {
      const pts = option.points || 10;
      setScore(prev => prev + pts);
      addMessage({ type: 'system', text: `✅ Correct! +${pts} points` });
      setTimeout(() => processTrainedStep(conv, stepIndex + 1, score + pts), 800);
    } else {
      addMessage({ type: 'system', text: '❌ Not quite. Try again!' });
      setWaitingForInput(true);
      setTrainedResponseOptions({ options: trainedResponseOptions.options, stepIndex, conv });
    }
  }, [trainedResponseOptions, addMessage, processTrainedStep, score]);

  const handleTrainedInput = useCallback(async (text) => {
    if (!currentTrainedConv) return;
    const step = currentTrainedConv.steps[currentStepIndex];
    if (!step) return;
    const stepType = step.type || 'exchange';
    const gText = (base, male) => (userGender === 'male' && male != null && male !== '') ? male : base;
    const newAttempts = stepAttempts + 1;
    setStepAttempts(newAttempts);
    setTrainedResponseOptions(null); // Clear choice buttons on any input

    // Helper: reveal correct answer and auto-advance after max attempts
    const revealAndAdvance = (correctAnswer, hint) => {
      addMessage({ type: 'system', text: `💡 The correct answer is: ${correctAnswer}` });
      if (hint) addMessage({ type: 'system', text: `📝 ${hint}` });
      setWaitingForInput(false);
      setUserInput('');
      setTimeout(() => processTrainedStep(currentTrainedConv, currentStepIndex + 1, score), 1200);
    };

    // Quiz type — check exact answer
    if (stepType === 'quiz') {
      addMessage({ type: 'user', text });
      if (text.toLowerCase().trim() === (step.correctAnswer || '').toLowerCase().trim()) {
        addMessage({ type: 'system', text: '✅ Correct!' });
        setScore(prev => prev + (step.points || 15));
        setWaitingForInput(false);
        setUserInput('');
        setTimeout(() => processTrainedStep(currentTrainedConv, currentStepIndex + 1, score + (step.points || 15)), 800);
      } else {
        // After 3 wrong attempts — reveal answer and move on
        if (newAttempts >= 3) {
          revealAndAdvance(step.correctAnswer || '?', step.hint);
        } else {
          addMessage({ type: 'system', text: `💡 Try again! Hint: ${step.correctAnswer?.charAt(0)}... (${3 - newAttempts} tries left)` });
          setUserInput('');
        }
      }
      return;
    }

    // Branching exchange — check against all branches
    if (step.branches && step.branches.length > 0) {
      addMessage({ type: 'user', text });
      const userIntent = detectIntent(text);
      const textLower = text.toLowerCase();
      // Find best matching branch
      let matchedBranch = null;
      for (const branch of step.branches) {
        const branchIntentMatch = branch.intent && userIntent === branch.intent;
        const branchKeywordMatch = (branch.keywords || []).some(kw => textLower.includes(kw.toLowerCase()));
        if (branchIntentMatch || branchKeywordMatch) {
          matchedBranch = branch;
          break;
        }
      }
      if (matchedBranch) {
        const pts = matchedBranch.points || 10;
        const successMsg = gText(step.success_message, step.success_message_male) || '';
        if (successMsg) addMessage({ type: 'system', text: successMsg });
        setScore(prev => prev + pts);
        setWaitingForInput(false);
        setUserInput('');
        const branchResponse = gText(matchedBranch.response, matchedBranch.response_male);
        setTimeout(() => {
          if (branchResponse) addMessage({ type: 'bot', text: branchResponse });
          // Find next step by id if branch has next_step_id, otherwise go linear
          let nextIdx = currentStepIndex + 1;
          if (matchedBranch.next_step_id) {
            const foundIdx = currentTrainedConv.steps.findIndex(s => s.id === matchedBranch.next_step_id);
            if (foundIdx >= 0) nextIdx = foundIdx;
          }
          // Listen prompt — ask user if they want to hear audio
          const listenAudio = gText(step.listenAudioUrl, step.listenAudioUrl_male);
          if (step.listenPrompt && listenAudio) {
            const promptText = gText(step.listenPromptText, step.listenPromptText_arabic) || 'Want to hear how it sounds?';
            setTimeout(() => {
              addMessage({ type: 'bot', text: promptText });
              setListenPromptData({ audioUrl: listenAudio, conv: currentTrainedConv, nextIdx, nextScore: score + pts });
            }, 600);
          } else {
            setTimeout(() => processTrainedStep(currentTrainedConv, nextIdx, score + pts), 800);
          }
        }, 500);
      } else {
        // After 3 wrong attempts — reveal answer and move on
        if (newAttempts >= 3) {
          const correctKeywords = step.branches[0]?.keywords?.join(' / ') || step.branches[0]?.response || '?';
          revealAndAdvance(correctKeywords, step.hint || step.branches[0]?.hint);
        } else {
          const fallback = gText(step.fallback_response, step.fallback_response_male);
          if (fallback) {
            addMessage({ type: 'bot', text: fallback });
          } else {
            const hint = step.branches[0]?.hint || step.hint || 'Try again!';
            addMessage({ type: 'system', text: `💡 ${hint} (${3 - newAttempts} tries left)` });
          }
          setUserInput('');
        }
      }
      return;
    }

    // Standard exchange — check intent or keyword match
    const userIntent = detectIntent(text);
    const intentMatch = step.expected_intent && userIntent === step.expected_intent;
    const keywordMatch = step.expected_keywords?.some(kw =>
      text.toLowerCase().includes(kw.toLowerCase())
    );

    if (intentMatch || keywordMatch) {
      addMessage({ type: 'user', text });
      const successMsg = gText(step.success_message, step.success_message_male) || '';
      if (successMsg) addMessage({ type: 'system', text: successMsg });
      const pts = step.points || 10;
      setScore(prev => prev + pts);
      setWaitingForInput(false);
      setUserInput('');

      // Show correct response with both Latin + Arabic so MessageBubble can switch dynamically
      const latinResponse = gText(step.correct_response, step.correct_response_male);
      const arabicResponse = gText(step.correct_response_arabic, step.correct_response_arabic_male);
      const correctAudio = gText(step.correctResponseAudioUrl, step.correctResponseAudioUrl_male);
      setTimeout(() => {
        if (latinResponse || arabicResponse) addMessage({ type: 'bot', text: latinResponse || arabicResponse, text_arabic: arabicResponse || undefined, translation: step.correct_translation || '', audioUrl: correctAudio || undefined });
        if (correctAudio) playAudio(correctAudio);
        // Listen prompt — ask user if they want to hear audio
        const listenAudio = gText(step.listenAudioUrl, step.listenAudioUrl_male);
        if (step.listenPrompt && listenAudio) {
          const promptText = gText(step.listenPromptText, step.listenPromptText_arabic) || 'Want to hear how it sounds?';
          setTimeout(() => {
            addMessage({ type: 'bot', text: promptText });
            setListenPromptData({ audioUrl: listenAudio, conv: currentTrainedConv, nextIdx: currentStepIndex + 1, nextScore: score + pts });
          }, 600);
        } else {
          setTimeout(() => processTrainedStep(currentTrainedConv, currentStepIndex + 1, score + pts), 800);
        }
      }, 500);
    } else {
      addMessage({ type: 'user', text });
      // After 3 wrong attempts — reveal answer and move on
      if (newAttempts >= 3) {
        const correctAnswer = step.expected_keywords?.[0] || step.correct_response || '?';
        revealAndAdvance(correctAnswer, step.hint);
      } else {
        addMessage({ type: 'system', text: `💡 ${step.hint || 'Try again!'} (${3 - newAttempts} tries left)` });
        setUserInput('');
      }
    }
  }, [currentTrainedConv, currentStepIndex, userGender, detectIntent, addMessage, playAudio, score, processTrainedStep, stepAttempts]);

  // Shared input handler — used by both Enter key and Send button
  const handleTextInput = useCallback(async (text) => {
    const actualUserInput = text.trim();
    if (!actualUserInput) return;

    // Route to trained mode if active
    if (trainedMode) {
      handleTrainedInput(actualUserInput);
      return;
    }

    const matchedChoice = smartMatchResponse(actualUserInput, choices);

    if (matchedChoice) {
      if (matchedChoice.isContinueChoice) {
        if (matchedChoice.id === 'continue_yes') {
          handleSmartIntentPause(actualUserInput, 'yes');
        } else {
          handleSmartIntentPause(actualUserInput, 'no');
        }
      } else {
        handleUserResponse(
          { userText: actualUserInput, matchedOption: matchedChoice },
          matchedChoice.isCorrect,
          hintUsed ? Math.max(0, (matchedChoice.points || 0) - 5) : (matchedChoice.points || 0)
        );
      }
    } else {
      const detectedIntent = detectIntent(actualUserInput);
      if (detectedIntent && ['greeting', 'goodbye', 'thanks', 'fine'].includes(detectedIntent)) {
        handleSmartIntentPause(actualUserInput, detectedIntent);
      } else {
        addMessage({ type: 'user', text: actualUserInput });

        // Wrong answer — show clue
        setWrongAttempts(prev => prev + 1);
        const correctOpt = choices?.find(c => c.isCorrect);
        const expectedIntent = correctOpt ? detectIntent(correctOpt.text || '') : null;
        const clues = {
          greeting: "Hmm, try a greeting! How would you say hello? 👋",
          fine: "Try telling me how you're doing! 😊",
          goodbye: "Try saying goodbye! 👋",
          thanks: "Try saying thank you! 🙏",
          introduction: "Try introducing yourself! 😄",
          ordering: "Try ordering something! 🍽️",
          please: "Try being polite! 🙏",
          apology: "Try apologizing! 😅"
        };
        const clue = (expectedIntent && clues[expectedIntent]) || "Not quite! Try again or use the hint 💡";
        addMessage({ type: 'system', text: `❌ ${clue}` });
        setUserInput('');
      }
    }
    setShowHint(false);
    setTypingHintText('');
  }, [smartMatchResponse, choices, handleSmartIntentPause, handleUserResponse, hintUsed, detectIntent, addMessage, trainedMode, handleTrainedInput, messages, selectedFlow, currentNodeId, getNextNodeFromEdges, delay, prefersReducedMotion, processNode]);

  const handleSubmitInput = useCallback(() => {
    if (!userInput.trim()) return;
    handleUserResponse(userInput.trim());
  }, [userInput, handleUserResponse]);

  const closeVocabulary = useCallback(async () => {
    setShowVocabulary(null);
    if (selectedFlow && currentNodeId) {
      const currentNode = selectedFlow.nodes?.find(n => n.id === currentNodeId);
      const nextNodeId = currentNode?.data?.nextNodeId || getNextNodeFromEdges(selectedFlow, currentNodeId);
      await processNode(selectedFlow, nextNodeId);
    }
  }, [selectedFlow, currentNodeId, getNextNodeFromEdges, processNode]);

  const handleBack = useCallback(() => {
    if (view === 'chat') {
      // Clean up trained mode if active
      if (trainedMode) {
        setTrainedMode(false);
        setCurrentTrainedConv(null);
        setCurrentStepIndex(0);
        setListenPromptData(null);
      }
      // If in a story, offer to continue or exit
      if (selectedStory) {
        setSelectedStory(null);
      }
      setView('bot-select');
      setSelectedFlow(null);
      setMessages([]);
      setConversationComplete(false);
    } else {
      onBack?.();
    }
  }, [view, onBack, trainedMode]);

  const currentBot = useMemo(() => {
    // For stories, get bot from the selected flow's bot_id
    if (selectedStory && selectedFlow && selectedFlow.bot_id) {
      if (bots[selectedFlow.bot_id]) return bots[selectedFlow.bot_id];
      // Fallback based on bot_gender
      if (selectedFlow.bot_gender === 'female') return DEFAULT_BOTS.female;
      return DEFAULT_BOTS.male;
    }
    // First try to find by bot ID
    if (bots[selectedBot]) return bots[selectedBot];
    // Fallback to DEFAULT_BOTS
    if (DEFAULT_BOTS[selectedBot]) return DEFAULT_BOTS[selectedBot];
    // Last resort - find by gender in current bots
    const botByGender = Object.values(bots).find(b => b.gender === selectedBot);
    if (botByGender) return botByGender;
    return DEFAULT_BOTS.female;
  }, [bots, selectedBot, selectedStory, selectedFlow]);

  // Loading state
  if (loading && view === 'bot-select') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{t('chat_loading_conversations')}</p>
        </div>
      </div>
    );
  }

  // CHAT VIEW - Fits entire screen without scrolling
  // Allow chat view if: (selectedBot is set) OR (we're in a story) OR (trained mode)
  if (view === 'chat' && (trainedMode || (selectedFlow && (selectedBot || selectedStory)))) {
    return (
      <div 
        className={`fixed inset-0 flex flex-col lg:flex-row z-[100] ${isDarkMode ? theme.chatBgDark : theme.chatBg}`}
        style={{ height: '100vh', maxHeight: '100vh', overflow: 'hidden' }}
      >
        
        {/* LEFT PANEL - Learning Aids (Desktop only) - Keep original dark style */}
        <aside 
          className={`hidden lg:flex lg:w-64 flex-col flex-shrink-0 ${isDarkMode ? 'bg-gray-800 border-r border-gray-700' : 'bg-white border-r border-gray-200'}`}
          style={{ height: '100vh', maxHeight: '100vh' }}
        >
          {/* Panel Header - Very Compact */}
          <div className={`flex-shrink-0 px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{trainedMode ? `🎓 ${currentTrainedConv?.title || 'Training'}` : `📖 ${selectedFlow?.name || ''}`}</h2>
          </div>
          
          {/* Stats - Inline */}
          <div className={`flex-shrink-0 px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex gap-3`}>
            <div className="flex items-center gap-1">
              <span>⭐</span>
              <span className={`font-bold ${isDarkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>{score}</span>
            </div>
            <div className="flex items-center gap-1">
              <span>🔥</span>
              <span className={`font-bold ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>{streak}</span>
            </div>
          </div>
          
          {/* Tip - if any */}
          {showTip && (
            <div className={`flex-shrink-0 px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className={`p-2 rounded text-xs ${
                showTip.type === 'warning' ? (isDarkMode ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-50 text-amber-700') :
                showTip.type === 'success' ? (isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700') :
                (isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700')
              }`}>
                💡 {showTip.text}
              </div>
            </div>
          )}
          
          {/* Dictionary Panel - Click word to see definition */}
          <div className={`flex-shrink-0 flex flex-col max-h-[70vh] overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            {/* Dictionary Header - Fixed */}
            <div className={`px-3 py-2 border-b flex-shrink-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <p className={`text-xs font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  📖 Dictionary
                </p>
                {(selectedDictWord || dictWords.length > 0) && (
                  <button 
                    onClick={() => { setSelectedDictWord(null); setDictWords([]); }}
                    className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                  >
                    ✕ Clear
                  </button>
                )}
              </div>
              
              {/* Hint: Click on chat bubble to see words */}
              {!selectedDictWord && dictWords.length === 0 && !aiDictSearching && (
                <p className={`text-xs italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  💡 Click on a chat bubble to see Darija words
                </p>
              )}
              
              {/* Loading indicator */}
              {aiDictSearching && (
                <div className="flex items-center gap-2">
                  <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  <p className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{t('chat_looking_up_words')}</p>
                </div>
              )}
            </div>
            
            {/* Dictionary Content - Scrollable */}
            <div className="flex-1 overflow-y-auto px-3 py-2">
            
            {/* Show multiple words from bubble */}
            {dictWords.length > 0 ? (
              <div className="space-y-2">
                <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  📚 Words in this bubble ({dictWords.length})
                </p>
                {dictWords.map((word, idx) => (
                  <div 
                    key={idx}
                    onClick={() => { setSelectedDictWord(word); }}
                    className={`p-3 rounded-xl cursor-pointer transition-all hover:scale-[1.01] ${
                      isDarkMode 
                        ? 'bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 border border-gray-600' 
                        : 'bg-gradient-to-r from-white to-gray-50 hover:from-gray-50 hover:to-gray-100 border border-gray-200 shadow-sm'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className={`font-bold text-base ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{word.darija}</p>
                          {word.arabic && (
                            <span className={`text-sm ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`} dir="rtl">{word.arabic}</span>
                          )}
                          {word.pydoda_verified && (
                            <span className="text-green-500 text-xs" title={t('chat_verified_label')}>✓</span>
                          )}
                        </div>
                        <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>{word.meaning}</p>
                        {word.phonetic && (
                          <p className={`text-xs italic mt-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{word.phonetic}</p>
                        )}
                      </div>
                      {word.wordType && (
                        <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${isDarkMode ? 'bg-purple-600/40 text-purple-300' : 'bg-purple-100 text-purple-600'}`}>
                          {word.wordType}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : selectedDictWord ? (
              <div 
                className={`p-3 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-purple-900/50 to-indigo-900/50 border border-purple-700' : 'bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200'}`}
              >
                <div className="space-y-3">
                  {/* Searched English Word */}
                  {selectedDictWord.searchedWord && (
                    <div className={`px-2 py-1 mb-2 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>🔍 SEARCHED</span>
                      <p className={`font-medium text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>&quot;{selectedDictWord.searchedWord}&quot;</p>
                    </div>
                  )}
                  
                  {/* Word Header with Audio */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className={`font-bold text-xl ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{selectedDictWord.darija}</p>
                        {selectedDictWord.audioUrl && (
                          <button 
                            onClick={() => playAudio(selectedDictWord.audioUrl)}
                            className={`p-1.5 rounded-full transition-all shadow-md ${isDarkMode ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-500 hover:bg-blue-600'} text-white`}
                            title={t('chat_play_pronunciation')}
                          >
                            🔊
                          </button>
                        )}
                      </div>
                      {/* English Translation */}
                      {selectedDictWord.english && (
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                          {selectedDictWord.english}
                        </p>
                      )}
                      <p className={`text-sm italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedDictWord.phonetic}</p>
                    </div>
                    {selectedDictWord.wordType && (
                      <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${isDarkMode ? 'bg-purple-600/50 text-purple-200' : 'bg-purple-200 text-purple-700'}`}>
                        {selectedDictWord.wordType}
                      </span>
                    )}
                  </div>
                  
                  {/* Category */}
                  {selectedDictWord.category && (
                    <div className={`px-2 py-1 rounded-lg ${isDarkMode ? 'bg-indigo-900/30' : 'bg-indigo-50'}`}>
                      <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>📂 CATEGORY: </span>
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-indigo-200' : 'text-indigo-700'}`}>{selectedDictWord.category}</span>
                    </div>
                  )}
                  
                  {/* Meaning */}
                  <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                    <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>📖 MEANING</span>
                    <p className={`font-medium ${isDarkMode ? 'text-blue-200' : 'text-blue-700'}`}>{selectedDictWord.meaning}</p>
                  </div>
                  
                  {/* Grammar Note */}
                  {selectedDictWord.grammarNote && (
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
                      <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>📝 GRAMMAR</span>
                      <p className={`text-sm ${isDarkMode ? 'text-amber-200' : 'text-amber-700'}`}>{selectedDictWord.grammarNote}</p>
                    </div>
                  )}
                  
                  {/* Sentence Structure */}
                  {selectedDictWord.sentenceStructure && (
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-cyan-900/30' : 'bg-cyan-50'}`}>
                      <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>🔤 STRUCTURE</span>
                      <p className={`text-sm ${isDarkMode ? 'text-cyan-200' : 'text-cyan-700'}`}>{selectedDictWord.sentenceStructure}</p>
                    </div>
                  )}
                  
                  {/* Conjugation */}
                  {selectedDictWord.conjugation && (
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-pink-900/30' : 'bg-pink-50'}`}>
                      <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`}>🔄 CONJUGATION</span>
                      <p className={`text-sm ${isDarkMode ? 'text-pink-200' : 'text-pink-700'}`}>{selectedDictWord.conjugation}</p>
                    </div>
                  )}
                  
                  {/* Examples */}
                  {selectedDictWord.examples && (
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                      <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>💬 EXAMPLES</span>
                      <div className="mt-1 space-y-1">
                        {(Array.isArray(selectedDictWord.examples) ? selectedDictWord.examples : [selectedDictWord.examples]).map((ex, i) => (
                          <p key={i} className={`text-sm ${isDarkMode ? 'text-green-200' : 'text-green-700'}`}>
                            • {typeof ex === 'string' ? ex : (ex.darija && ex.english ? `${ex.darija} = ${ex.english}` : JSON.stringify(ex))}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Cultural Context */}
                  {selectedDictWord.culturalNote && (
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-orange-900/30' : 'bg-orange-50'}`}>
                      <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>🌍 CULTURAL TIP</span>
                      <p className={`text-sm ${isDarkMode ? 'text-orange-200' : 'text-orange-700'}`}>{selectedDictWord.culturalNote}</p>
                    </div>
                  )}
                  
                  {/* Gender Rules */}
                  {selectedDictWord.genderNote && (
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-violet-900/30' : 'bg-violet-50'}`}>
                      <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>♀♂ GENDER</span>
                      <p className={`text-sm ${isDarkMode ? 'text-violet-200' : 'text-violet-700'}`}>{selectedDictWord.genderNote}</p>
                    </div>
                  )}
                  
                  {/* Related Words */}
                  {selectedDictWord.relatedWords && selectedDictWord.relatedWords.length > 0 && (
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>🔗 RELATED</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedDictWord.relatedWords.map((rw, i) => (
                          <span key={i} className={`px-2 py-0.5 rounded-full text-xs ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>
                            {typeof rw === 'string' ? rw : (rw.word || rw.darija || JSON.stringify(rw))}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                💡 Click <span className="border-b border-dotted border-gray-400">underlined words</span> or the 📖 button to see meanings
              </p>
            )}
            </div>
          </div>
          
          {/* Words Learned - Scrollable */}
          <div className="flex-1 overflow-y-auto px-3 py-2 min-h-0">
            <p className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>📚 Words ({summary.length})</p>
            {summary.length > 0 ? (
              <div className="space-y-1">
                {summary.map((item, idx) => (
                  <div key={idx} className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'}`}>{item}</div>
                ))}
              </div>
            ) : (
              <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('chat_words_appear_here')}</p>
            )}
          </div>
          
          {/* Achievement - if any */}
          {showAchievement && (
            <div className={`flex-shrink-0 px-3 py-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className={`p-2 rounded text-center ${isDarkMode ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
                <span className="text-2xl">{showAchievement.icon}</span>
                <p className={`text-xs font-bold ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{showAchievement.name}</p>
              </div>
            </div>
          )}
          
          {/* Progress Bar - Always Visible - Uses Theme */}
          <div className={`flex-shrink-0 px-3 py-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex justify-between text-xs mb-1">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{t('chat_progress')}</span>
              <span className={isDarkMode ? 'text-gray-500' : 'text-gray-500'}>{messages.filter(m => m.type === 'user').length} replies</span>
            </div>
            <div className={`h-1.5 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div 
                className={`h-full rounded-full bg-gradient-to-r ${theme.gradient} transition-all`}
                style={{ width: conversationComplete ? '100%' : `${Math.min(messages.length * 15, 90)}%` }}
              />
            </div>
          </div>
        </aside>
        
        {/* RIGHT PANEL - Chat Area - Full Height */}
        <div className="flex-1 flex flex-col min-h-0" style={{ height: '100vh', maxHeight: '100vh' }}>
          {/* Chat Header - Compact - Uses Theme */}
          <header className={`flex-shrink-0 ${isDarkMode ? 'bg-gray-800' : theme.headerBg}`}>
            <div className="flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <button onClick={handleBack} className="p-1 rounded-full hover:bg-white/10">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden"
                  style={{ background: `linear-gradient(135deg, ${currentBot.color === 'pink' ? '#ec4899, #f43f5e' : currentBot.color === 'blue' ? '#3b82f6, #6366f1' : currentBot.color === 'purple' ? '#a855f7, #8b5cf6' : '#3b82f6, #6366f1'})` }}
                >
                  {currentBot.avatarUrl ? (
                    <img src={currentBot.avatarUrl} alt={currentBot.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <span className="text-base">{currentBot.icon}</span>
                  )}
                </div>
                <div className="min-w-0">
                  <h1 className="font-bold text-white text-sm truncate">{currentBot.name}</h1>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <div className="flex bg-white/20 rounded-full p-0.5">
                  <button onClick={() => setScriptType('latin')} className={`px-2 py-0.5 rounded-full text-xs ${scriptType === 'latin' ? 'bg-white text-blue-600' : 'text-white'}`}>{t('chat_latin_button')}</button>
                  <button onClick={() => setScriptType('arabic')} className={`px-2 py-0.5 rounded-full text-xs ${scriptType === 'arabic' ? 'bg-white text-purple-600' : 'text-white'}`}>عربي</button>
                </div>
                <div className="lg:hidden flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded-full">
                  <span className="text-yellow-300 text-xs">⭐</span>
                  <span className="text-white font-bold text-xs">{score}</span>
                </div>
              </div>
            </div>
          </header>

          {/* Chat Messages - Scrollable, Takes Available Space */}
          <div 
            ref={chatContainerRef}
            className={`flex-1 overflow-y-auto py-2 min-h-0 ${isDarkMode ? 'bg-gray-900' : ''}`}
            style={{ backgroundImage: isDarkMode ? 'none' : 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%239C92AC\' fill-opacity=\'0.03\'%3E%3Cpath d=\'M20 20v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z\'/%3E%3C/g%3E%3C/svg%3E")' }}
          >
            <div className="max-w-lg mx-auto px-3">
              {messages.map((msg) => (
                  <MessageBubble
                    key={msg.id}
                    msg={msg}
                    bot={currentBot}
                    scriptType={scriptType}
                    isDarkMode={isDarkMode}
                    selectedBot={selectedBot}
                    onPlayAudio={playAudio}
                    vocabulary={allVocabulary}
                    onWordClick={handleWordClick}
                    onShowAllWords={handleShowAllWords}
                    onBubbleClick={extractWordsFromBubble}
                    userAvatarUrl={accountAvatarUrl}
                    userName={accountName}
                    theme={theme}
                  />
              ))}
              {isTyping && <TypingIndicator bot={currentBot} isDarkMode={isDarkMode} selectedBot={selectedBot} />}
            </div>
          </div>

          {/* Mobile tip */}
          {showTip && (
            <div className={`lg:hidden flex-shrink-0 px-3 py-1 text-xs ${showTip.type === 'warning' ? 'bg-amber-500' : showTip.type === 'success' ? 'bg-green-500' : 'bg-blue-500'} text-white`}>
              💡 {showTip.text}
            </div>
          )}

          {/* MOBILE DICTIONARY SLIDE-IN PANEL */}
          {isMobileDictOpen && (
            <div className="lg:hidden fixed inset-0 z-[110]">
              {/* Backdrop */}
              <div 
                className="absolute inset-0 bg-black/50" 
                onClick={() => setIsMobileDictOpen(false)}
              />
              {/* Slide-in Panel from Right */}
              <div 
                className={`absolute right-0 top-0 bottom-0 w-[85%] max-w-sm flex flex-col shadow-2xl animate-slide-in-right ${
                  isDarkMode ? 'bg-gray-800' : 'bg-white'
                }`}
              >
                {/* Panel Header */}
                <div className={`flex-shrink-0 flex items-center justify-between px-4 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    📖 Dictionary
                  </h3>
                  <button 
                    onClick={() => setIsMobileDictOpen(false)}
                    className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Panel Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4">
                  {aiDictSearching ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                      <p className={`ml-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t('chat_looking_up_words')}</p>
                    </div>
                  ) : dictWords.length > 0 ? (
                    <div className="space-y-3">
                      <p className={`text-sm font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        📚 Words found ({dictWords.length})
                      </p>
                      {dictWords.map((word, idx) => (
                        <div 
                          key={idx}
                          className={`p-4 rounded-xl transition-all ${
                            isDarkMode 
                              ? 'bg-gradient-to-r from-gray-700 to-gray-800 border border-gray-600' 
                              : 'bg-gradient-to-r from-white to-gray-50 border border-gray-200 shadow-sm'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{word.darija}</p>
                                {word.arabic && (
                                  <span className={`text-sm ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`} dir="rtl">{word.arabic}</span>
                                )}
                                {word.pydoda_verified && (
                                  <span className="text-green-500 text-xs" title={t('chat_verified_label')}>✓</span>
                                )}
                              </div>
                              <p className={`text-base ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>{word.meaning}</p>
                              {word.phonetic && (
                                <p className={`text-sm italic mt-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{word.phonetic}</p>
                              )}
                            </div>
                            {word.wordType && (
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${isDarkMode ? 'bg-purple-600/40 text-purple-300' : 'bg-purple-100 text-purple-600'}`}>
                                {word.wordType}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : selectedDictWord ? (
                    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-purple-900/50 to-indigo-900/50 border border-purple-700' : 'bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200'}`}>
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className={`font-bold text-xl ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{selectedDictWord.darija}</p>
                            <p className={`text-sm italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedDictWord.phonetic}</p>
                          </div>
                          {selectedDictWord.wordType && (
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${isDarkMode ? 'bg-purple-600/50 text-purple-200' : 'bg-purple-200 text-purple-700'}`}>
                              {selectedDictWord.wordType}
                            </span>
                          )}
                        </div>
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                          <span className={`text-xs font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>📖 MEANING</span>
                          <p className={`font-medium ${isDarkMode ? 'text-blue-200' : 'text-blue-700'}`}>{selectedDictWord.meaning}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className={`text-4xl mb-3`}>💬</p>
                      <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Tap on a chat bubble to see Darija words and their meanings
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Close Button at Bottom */}
                <div className={`flex-shrink-0 p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <button
                    onClick={() => { setIsMobileDictOpen(false); setDictWords([]); setSelectedDictWord(null); }}
                    className={`w-full py-3 rounded-xl font-bold transition-all ${
                      isDarkMode 
                        ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    ✕ Close Dictionary
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Completion - Simple buttons at bottom */}
          {conversationComplete && !showQuizModal && (
            <div className={`flex-shrink-0 p-3 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="max-w-lg mx-auto flex gap-2">
                <button
                  onClick={() => trainedMode ? startTrainedConversation(currentTrainedConv) : startConversation(selectedFlow)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                >
                  🔄 Restart
                </button>
                {/* Show Quiz button only if quiz is enabled */}
                {!trainedMode && selectedFlow?.quiz_enabled !== false && (
                  <button 
                    onClick={() => {
                      // Check for custom quiz questions first
                      const customQuestions = selectedFlow?.custom_quiz_questions || [];
                      if (customQuestions.length > 0) {
                        // Use custom questions
                        setQuizQuestions(customQuestions.map(q => ({
                          word: q.word || q.question,
                          question: q.question,
                          correctAnswer: q.correctAnswer,
                          options: q.options || [q.correctAnswer]
                        })));
                        setCurrentQuizIndex(0);
                        setQuizScore(0);
                        setQuizComplete(false);
                        setShowQuizModal(true);
                        return;
                      }
                      
                      // Generate quiz from vocabulary
                      const flowVocab = selectedFlow?.vocabulary || [];
                      const nodeVocab = selectedFlow?.nodes?.flatMap(n => n.data?.vocabulary || []) || [];
                      const allVocab = [...flowVocab, ...nodeVocab].filter(v => v.darija && v.meaning);
                      
                      if (allVocab.length >= 2) {
                        // Determine number of options based on quiz difficulty
                        const difficulty = selectedFlow?.quiz_difficulty || 'auto';
                        const optionCount = difficulty === 'easy' ? 2 : difficulty === 'hard' ? 4 : 3;
                        
                        const shuffled = allVocab.sort(() => Math.random() - 0.5).slice(0, Math.min(5, allVocab.length));
                        const questions = shuffled.map(v => ({
                          word: v.darija,
                          question: `What does "${v.darija}" mean?`,
                          correctAnswer: v.meaning,
                          options: [v.meaning, ...allVocab.filter(x => x.meaning !== v.meaning).slice(0, optionCount - 1).map(x => x.meaning)].sort(() => Math.random() - 0.5)
                        }));
                        setQuizQuestions(questions);
                        setCurrentQuizIndex(0);
                        setQuizScore(0);
                        setQuizComplete(false);
                        setShowQuizModal(true);
                      } else {
                        addMessage({ type: 'system', text: '📚 Not enough vocabulary for quiz yet!' });
                      }
                    }}
                    className="flex-1 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl text-sm font-bold shadow-lg hover:shadow-xl transition-all"
                  >
                    📚 Quiz
                  </button>
                )}
                <button 
                  onClick={handleBack} 
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                >
                  📖 More
                </button>
              </div>
            </div>
          )}

          {/* Gender Selection — asked at start of trained conversations */}
          {trainedGenderPending && (
            <div className={`flex-shrink-0 p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="max-w-lg mx-auto">
                <p className={`text-center text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Choose who you're speaking to:
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => handleGenderSelect('female')}
                    className="flex-1 max-w-[160px] py-3 rounded-xl text-sm font-medium border-2 border-pink-500 bg-pink-500/10 text-pink-400 hover:bg-pink-500/30 transition-colors"
                  >
                    ♀ Female
                  </button>
                  <button
                    onClick={() => handleGenderSelect('male')}
                    className="flex-1 max-w-[160px] py-3 rounded-xl text-sm font-medium border-2 border-blue-500 bg-blue-500/10 text-blue-400 hover:bg-blue-500/30 transition-colors"
                  >
                    ♂ Male
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Listen Prompt — Yes/Skip buttons after correct answer */}
          {listenPromptData && (
            <div className={`flex-shrink-0 p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="max-w-lg mx-auto">
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => handleListenPromptChoice('yes')}
                    className="flex-1 max-w-[180px] py-3 rounded-xl text-sm font-medium border-2 border-green-500 bg-green-500/10 text-green-400 hover:bg-green-500/30 transition-colors"
                  >
                    🔊 Yes, play it!
                  </button>
                  <button
                    onClick={() => handleListenPromptChoice('skip')}
                    className="flex-1 max-w-[180px] py-3 rounded-xl text-sm font-medium border-2 border-gray-500 bg-gray-500/10 text-gray-400 hover:bg-gray-500/30 transition-colors"
                  >
                    Skip
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Response Options — shown for Response node multiple choice */}
          {trainedResponseOptions && trainedResponseOptions.options?.length > 0 && (
            <div className={`flex-shrink-0 p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="max-w-lg mx-auto">
                <div className="flex flex-wrap gap-2 justify-center">
                  {trainedResponseOptions.options.map((opt, idx) => (
                    <button
                      key={opt.id || idx}
                      onClick={() => handleResponseOption(opt)}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-colors ${
                        isDarkMode
                          ? 'border-amber-500 bg-amber-500/10 text-amber-400 hover:bg-amber-500/30'
                          : 'border-amber-500 bg-amber-50 text-amber-700 hover:bg-amber-100'
                      }`}
                    >
                      {opt.text}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Input/Choices - Fixed at Bottom */}
          {waitingForInput && !conversationComplete && (
            <div className={`flex-shrink-0 p-3 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="max-w-lg mx-auto space-y-2">
                
                {/* Typing Hint Display - Only shows when user clicks hint button */}
                {showHint && typingHintText && (
                  <div className={`p-2 rounded-lg text-sm animate-pulse ${isDarkMode ? 'bg-amber-900/30 text-amber-200 border border-amber-700' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span>💡</span>
                        <span>{t('chat_hint_prefix')} <strong>{typingHintText}</strong>...</span>
                      </div>
                      <button 
                        onClick={() => { setShowHint(false); setTypingHintText(''); }}
                        className="text-xs opacity-60 hover:opacity-100"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Smart Bot indicator — show when bot uses free typing */}
                {bots[selectedBot]?.is_smart_bot && waitingForInput && !showChoices && (
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${isDarkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-50 text-purple-600'}`}>
                    <span>⌨️</span>
                    <span>Type your answer — try using Darija words!</span>
                  </div>
                )}

                {/* Always Visible: Typing Input */}
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && userInput.trim()) {
                        handleTextInput(userInput);
                      }
                    }}
                    placeholder="Type your answer in Darija..."
                    className={`flex-1 p-3 rounded-xl border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400'}`}
                  />
                  {/* Hint Button - shows 40% of the answer when clicked */}
                  <button
                    onClick={() => {
                      if (choices.length > 0 && !showHint) {
                        const correctChoice = choices.find(c => c.isCorrect) || choices[0];
                        const answerText = correctChoice.text;
                        // Show 40% of the answer to help construct sentence
                        const hintLength = Math.max(3, Math.ceil(answerText.length * 0.4));
                        const hint = answerText.substring(0, hintLength);
                        setTypingHintText(hint);
                        setShowHint(true);
                        setHintUsed(true);
                      }
                    }}
                    disabled={showHint}
                    className={`p-3 rounded-xl transition-all ${
                      showHint 
                        ? isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-100 text-gray-400'
                        : isDarkMode ? 'bg-amber-600 hover:bg-amber-500 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'
                    }`}
                    title={t('chat_need_help_tooltip')}
                  >
                    💡
                  </button>
                  {/* Send Button - Uses Theme */}
                  <button
                    onClick={() => {
                      if (userInput.trim()) {
                        handleTextInput(userInput);
                      }
                    }}
                    className={`p-3 bg-gradient-to-r ${theme.gradient} text-white rounded-xl shadow-lg hover:shadow-xl transition-all`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>
                </div>
                
                {/* Show/Hide Choices Toggle - Uses Theme */}
                {choices.length > 0 && (
                  <button
                    onClick={() => setShowChoicesPanel(!showChoicesPanel)}
                    className={`w-full p-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                      isDarkMode 
                        ? `bg-gray-700 hover:bg-gray-600 ${theme.accent} border border-gray-600` 
                        : `${theme.buttonSecondary} border ${theme.border}`
                    }`}
                  >
                    <span>{showChoicesPanel ? '🙈' : '👁️'}</span>
                    {showChoicesPanel ? 'Hide Choices' : 'Show Choices'} ({choices.length} options)
                  </button>
                )}
                
                {/* Choices Panel - Hidden by default, shown when toggled */}
                {showChoicesPanel && choices.length > 0 && (
                  <div className="space-y-2 pt-2 border-t border-dashed ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}">
                    {choices.map((choice) => {
                      const flowVocab = selectedFlow?.vocabulary || [];
                      const nodeVocab = selectedFlow?.nodes?.flatMap(n => n.data?.vocabulary || []) || [];
                      const allVocabulary = [...flowVocab, ...nodeVocab];
                      
                      const choiceWords = [];
                      if (choice.text && allVocabulary.length > 0) {
                        const textLower = choice.text.toLowerCase();
                        allVocabulary.forEach(v => {
                          if (v.darija && textLower.includes(v.darija.toLowerCase())) {
                            choiceWords.push(v);
                          }
                        });
                      }
                      
                      return (
                        <div key={choice.id} className="relative">
                          <button
                            onClick={() => {
                              // Check if this is a continue choice from smart intent pause
                              if (choice.isContinueChoice) {
                                if (choice.id === 'continue_yes') {
                                  handleSmartIntentPause(choice.text, 'yes');
                                } else {
                                  handleSmartIntentPause(choice.text, 'no');
                                }
                              } else {
                                handleUserResponse(choice.text, choice.isCorrect, hintUsed ? Math.max(0, (choice.points || 0) - 5) : (choice.points || 0));
                              }
                            }}
                            className={`w-full p-3 pr-12 rounded-xl text-left transition-all hover:scale-[1.005] active:scale-[0.995] ${
                              isDarkMode 
                                ? 'bg-gradient-to-r from-gray-700 to-gray-750 hover:from-gray-650 hover:to-gray-700 text-white border border-gray-600 shadow-md' 
                                : 'bg-white hover:bg-gray-50 text-gray-800 border border-gray-200 shadow-sm hover:shadow-md'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className={`font-medium ${scriptType === 'arabic' ? 'text-right w-full' : ''}`}
                                    dir={scriptType === 'arabic' ? 'rtl' : 'ltr'}>
                                {scriptType === 'arabic' && choice.text_arabic ? choice.text_arabic : choice.text}
                              </span>
                              {choice.points > 0 && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-600'}`}>
                                  +{choice.points}
                                </span>
                              )}
                            </div>
                            {choice.translation && (
                              <div className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {choice.translation}
                              </div>
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (choiceWords.length > 0) {
                                setDictWords(choiceWords);
                              } else {
                                setDictWords([{ darija: choice.text, meaning: choice.translation || 'No translation', phonetic: '' }]);
                              }
                              setSelectedDictWord(null);
                            }}
                            className={`absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-sm transition-all ${
                              isDarkMode ? 'bg-purple-900/50 text-purple-300 hover:bg-purple-800/50' : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                            }`}
                            title={t('chat_see_in_dictionary')}
                          >
                            📖
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Quiz Modal Popup */}
        {showQuizModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">📚</span>
                    <h3 className="font-bold text-lg">{t('chat_vocabulary_quiz')}</h3>
                  </div>
                  <button 
                    onClick={() => setShowQuizModal(false)}
                    className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center justify-between mt-2 text-sm">
                  <span>{t('chat_question_of').replace('{0}', currentQuizIndex + 1).replace('{1}', quizQuestions.length)}</span>
                  <span className="font-bold">{t('chat_score_prefix')} {quizScore}</span>
                </div>
                {/* Progress bar */}
                <div className="mt-2 h-1.5 bg-white/30 rounded-full">
                  <div 
                    className="h-full bg-white rounded-full transition-all duration-300"
                    style={{ width: `${((currentQuizIndex + (quizComplete ? 1 : 0)) / quizQuestions.length) * 100}%` }}
                  />
                </div>
              </div>
              
              {/* Quiz Content */}
              {!quizComplete ? (
                <div className="p-5 space-y-4">
                  <div className={`text-center p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-purple-600'}`}>{t('chat_what_does_this_mean')}</p>
                    <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {quizQuestions[currentQuizIndex]?.word}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    {quizQuestions[currentQuizIndex]?.options.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          const isCorrect = option === quizQuestions[currentQuizIndex].correctAnswer;
                          if (isCorrect) {
                            setQuizScore(prev => prev + 10);
                            // Show flying congratulations
                            setFlyingCongrats(true);
                            setTimeout(() => setFlyingCongrats(false), 2000);
                          }
                          
                          // Next question or finish after short delay
                          setTimeout(() => {
                            if (currentQuizIndex < quizQuestions.length - 1) {
                              setCurrentQuizIndex(prev => prev + 1);
                            } else {
                              setQuizComplete(true);
                            }
                          }, isCorrect ? 800 : 500);
                        }}
                        className={`w-full p-4 rounded-xl text-left font-medium transition-all hover:scale-[1.02] active:scale-[0.98] ${
                          isDarkMode 
                            ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600' 
                            : 'bg-gray-50 hover:bg-gray-100 text-gray-800 border border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center space-y-4">
                  <div className="text-6xl">🏆</div>
                  <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    Quiz Complete!
                  </h3>
                  <div className={`inline-block px-6 py-3 rounded-xl ${
                    quizScore >= quizQuestions.length * 7 
                      ? 'bg-green-100 text-green-700' 
                      : quizScore >= quizQuestions.length * 5 
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-amber-100 text-amber-700'
                  }`}>
                    <p className="text-3xl font-bold">{quizScore} / {quizQuestions.length * 10}</p>
                    <p className="text-sm mt-1">
                      {quizScore >= quizQuestions.length * 7 ? '🌟 Excellent!' : quizScore >= quizQuestions.length * 5 ? '👍 Good job!' : '📚 Keep practicing!'}
                    </p>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        setCurrentQuizIndex(0);
                        setQuizScore(0);
                        setQuizComplete(false);
                      }}
                      className={`flex-1 py-3 rounded-xl font-bold transition-all ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                    >
                      🔄 Try Again
                    </button>
                    <button
                      onClick={() => setShowQuizModal(false)}
                      className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold shadow-lg"
                    >
                      ✓ Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Flying Congratulations Animation */}
        {flyingCongrats && (
          <div className="fixed inset-0 pointer-events-none z-[60] overflow-hidden">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="absolute text-4xl animate-bounce"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: '0.8s'
                }}
              >
                {['🎉', '⭐', '✨', '🌟', '💫', '🎊'][i % 6]}
              </div>
            ))}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-5xl font-bold text-green-500 animate-pulse drop-shadow-lg bg-white/90 px-8 py-4 rounded-2xl">
                ✅ Correct!
              </div>
            </div>
          </div>
        )}

        {/* Vocabulary Modal - REMOVED (using left panel dictionary instead) */}

        {/* Achievement Modal - Mobile */}
        {showAchievement && (
          <div className="lg:hidden fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-xs rounded-xl p-4 text-center ${isDarkMode ? 'bg-amber-900' : 'bg-amber-100'}`}>
              <span className="text-4xl">{showAchievement.icon}</span>
              <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{showAchievement.name}</p>
              <p className="text-amber-400 font-bold">+{showAchievement.points} pts</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // BOT SELECTION VIEW
  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[200] p-4 backdrop-blur-sm">
          <div className={`w-full max-w-md rounded-3xl p-6 shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>⚙️ Chat Settings</h2>
              <button onClick={() => setShowSettings(false)} className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            
            {/* Theme Selection - Fixed colors */}
            <div className="mb-6">
              <label className={`text-sm font-medium block mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('chat_chat_theme')}</label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(CHAT_THEMES).map(([key, t]) => {
                  const isSelected = chatTheme === key;
                  const bgColors = {
                    default: 'bg-blue-500',
                    ocean: 'bg-cyan-500',
                    sunset: 'bg-orange-500',
                    forest: 'bg-green-500',
                    lavender: 'bg-purple-500',
                    rose: 'bg-pink-500'
                  };
                  const gradientColors = {
                    default: 'from-blue-400 to-indigo-500',
                    ocean: 'from-cyan-400 to-teal-500',
                    sunset: 'from-orange-400 to-red-500',
                    forest: 'from-green-400 to-emerald-500',
                    lavender: 'from-purple-400 to-violet-500',
                    rose: 'from-pink-400 to-rose-500'
                  };
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        setChatTheme(key);
                        saveUserPreferences({ chat_theme: key });
                      }}
                      className={`p-3 rounded-xl text-center transition-all ${isSelected 
                        ? `${bgColors[key]} text-white ring-2 ring-white/50` 
                        : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full mx-auto mb-1 bg-gradient-to-br ${gradientColors[key]}`} />
                      <span className="text-xs font-medium">{t.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Stats */}
            {userProgress && (
              <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <h3 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('chat_your_progress_h')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-white'}`}>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{userProgress.total_conversations || 0}</p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('chat_conversations_label')}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-white'}`}>
                    <p className={`text-2xl font-bold text-yellow-500`}>{userProgress.total_score || 0}</p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('chat_total_score_label')}</p>
                  </div>
                  <div className={`p-3 rounded-lg col-span-2 ${isDarkMode ? 'bg-gray-600' : 'bg-white'}`}>
                    <p className={`text-2xl font-bold text-green-500`}>{userProgress.vocabulary_learned?.length || 0}</p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('chat_words_learned')}</p>
                  </div>
                </div>
              </div>
            )}
            
            <button
              onClick={() => {
                saveUserPreferences({ chat_theme: chatTheme });
                setShowSettings(false);
              }}
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-bold"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Header - on mobile: not sticky (just normal flow below main header), on desktop: sticky at top */}
      <header className={`lg:sticky lg:top-0 z-10 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={handleBack} className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <div>
                <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>💬 Conversation Challenge</h1>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('chat_practice_darija')}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
        {/* User Profile & Progress Card */}
        {(accountName || accountAvatarUrl || (userProgress && userProgress.total_conversations > 0)) && (
          <div className={`mb-6 p-4 rounded-2xl ${isDarkMode ? 'bg-gradient-to-r from-purple-900/50 to-indigo-900/50 border border-purple-700/50' : 'bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* User Avatar */}
                <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg ring-2 ring-white/30">
                  {accountAvatarUrl ? (
                    <img src={accountAvatarUrl} alt={accountName || 'You'} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xl font-bold">{accountName?.charAt(0)?.toUpperCase() || '?'}</span>
                  )}
                </div>
                <div>
                  <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {accountName ? `${accountName}'s Progress` : 'Your Progress'}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                    {userProgress?.completed_flows?.length || 0} completed • {userProgress?.vocabulary_learned?.length || 0} words learned
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-yellow-500">⭐ {userProgress?.total_score || 0}</p>
                <button
                  onClick={() => setShowSettings(true)}
                  className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                  title={t('chat_settings_tooltip')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs: Conversations / Stories */}
        <div className="mb-6">
          <div className={`inline-flex rounded-xl p-1 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <button 
              onClick={() => setActiveTab('conversations')} 
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                activeTab === 'conversations' 
                  ? 'bg-blue-500 text-white shadow-md' 
                  : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <span>💬</span> Conversations
            </button>
            <button 
              onClick={() => setActiveTab('stories')} 
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                activeTab === 'stories' 
                  ? 'bg-purple-500 text-white shadow-md' 
                  : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <span>📖</span> Stories {stories.length > 0 && <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{stories.length}</span>}
            </button>
          </div>
        </div>

        {/* STORIES TAB */}
        {activeTab === 'stories' && (
          <section>
            <h2 className={`text-sm font-semibold mb-3 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>📖 Story Mode - Multi-Chapter Adventures</h2>
            {stories.length === 0 ? (
              <div className={`text-center py-12 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white border border-gray-100'}`}>
                <div className="text-5xl mb-4">📚</div>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{t('chat_no_stories')}</p>
                <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t('chat_check_back_soon')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {stories.map(story => (
                  <div
                    key={story.id}
                    className={`w-full text-left p-5 rounded-2xl transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-gradient-to-br from-purple-900/80 to-indigo-900/80 border border-purple-700/50' 
                        : 'bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                        <span className="text-3xl">📖</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {story.name}
                          </h3>
                          {!story.is_free && (
                            <span className="px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs rounded-full font-bold flex-shrink-0">
                              PRO
                            </span>
                          )}
                        </div>
                        <p className={`text-sm line-clamp-2 mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {story.description}
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            story.difficulty === 'Beginner' 
                              ? isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700'
                              : story.difficulty === 'Intermediate'
                              ? isDarkMode ? 'bg-yellow-900/50 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                              : isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700'
                          }`}>
                            {story.difficulty}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                            📚 {story.chapters?.length || 0} chapters
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                            ⏱️ {story.total_duration || 15} min
                          </span>
                        </div>
                        {/* Play Button */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            // Immediate feedback
                            console.log('=== PLAY BUTTON CLICKED ===');
                            
                            const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
                            
                            fetch(`${apiUrl}/api/conversation-challenge/stories/${story.id}`)
                              .then(response => response.json())
                              .then(fullStory => {
                                console.log('Story loaded:', fullStory.name);
                                
                                if (!fullStory.chapter_flows || fullStory.chapter_flows.length === 0) {
                                  showNotification('This story has no chapters yet. Add flows as chapters in the admin editor.', 'warning');
                                  return;
                                }
                                const firstChapter = fullStory.chapter_flows[0];
                                if (!firstChapter.nodes || firstChapter.nodes.length === 0) {
                                  showNotification('The first chapter has no content yet.', 'warning');
                                  return;
                                }

                                // Set all state
                                setSelectedStory(fullStory);
                                setStoryChapterIndex(0);
                                setSelectedFlow(firstChapter);
                                setMessages([]);
                                setScore(0);
                                setStreak(0);
                                setConversationComplete(false);
                                setSummary([]);
                                setView('chat');

                                // Process first node after state is set
                                setTimeout(() => {
                                  const startNode = firstChapter.nodes?.find(n => n.type === 'start');
                                  if (startNode) {
                                    const firstEdge = firstChapter.edges?.find(e => e.source === startNode.id);
                                    if (firstEdge) {
                                      processNode(firstChapter, firstEdge.target);
                                    }
                                  }
                                }, 100);
                              })
                              .catch(err => {
                                console.error('Error loading story:', err);
                                showNotification('Failed to load story. Please try again.', 'error');
                              });
                          }}
                          className="mt-3 w-full py-3 px-4 rounded-lg font-bold text-base transition-all bg-green-500 hover:bg-green-600 text-white shadow-lg"
                        >
                          ▶ PLAY STORY
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* CONVERSATIONS TAB - Split Layout */}
        {activeTab === 'conversations' && (
          <div className={`flex gap-4 rounded-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800/50' : 'bg-white shadow-sm border border-gray-100'}`}>
            {/* LEFT SIDE - Bot Avatars */}
            <div className={`w-24 sm:w-32 flex-shrink-0 p-1 sm:p-0 ${isDarkMode ? 'bg-gray-800 border-r border-gray-700' : 'bg-gray-50 border-r border-gray-200'}`}>
              {/* Header with Script Toggle Tabs */}
              <div className="mb-2 px-1 pt-2">
                <p className={`text-[10px] font-semibold uppercase tracking-wide text-center mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('chat_partners')}</p>
                {/* Script Toggle as Tabs */}
                <div className={`flex rounded-lg p-0.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <button 
                    onClick={() => {
                      setScriptType('latin');
                      saveUserPreferences({ preferred_script: 'latin' });
                    }} 
                    className={`flex-1 px-2 py-1 rounded-md text-[9px] font-medium transition-all ${scriptType === 'latin' ? 'bg-blue-500 text-white shadow-sm' : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    🔤 Latin
                  </button>
                  <button 
                    onClick={() => {
                      setScriptType('arabic');
                      saveUserPreferences({ preferred_script: 'arabic' });
                    }} 
                    className={`flex-1 px-2 py-1 rounded-md text-[9px] font-medium transition-all ${scriptType === 'arabic' ? 'bg-purple-500 text-white shadow-sm' : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    عربي
                  </button>
                </div>
              </div>
              
              {/* Bot Avatars - Bigger */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {Object.entries(bots).map(([key, bot]) => {
                  const botId = bot.id || key;
                  const botColor = bot.color || (bot.gender === 'female' ? 'pink' : 'blue');
                  const colorStyles = {
                    pink: { selected: 'ring-pink-500 bg-pink-500/20', dot: 'bg-pink-500' },
                    blue: { selected: 'ring-blue-500 bg-blue-500/20', dot: 'bg-blue-500' },
                    purple: { selected: 'ring-purple-500 bg-purple-500/20', dot: 'bg-purple-500' },
                    green: { selected: 'ring-green-500 bg-green-500/20', dot: 'bg-green-500' },
                    orange: { selected: 'ring-orange-500 bg-orange-500/20', dot: 'bg-orange-500' },
                    teal: { selected: 'ring-teal-500 bg-teal-500/20', dot: 'bg-teal-500' },
                    red: { selected: 'ring-red-500 bg-red-500/20', dot: 'bg-red-500' },
                    indigo: { selected: 'ring-indigo-500 bg-indigo-500/20', dot: 'bg-indigo-500' }
                  };
                  const colors = colorStyles[botColor] || colorStyles.blue;
                  const isSelected = selectedBot === botId;
                  
                  // Count conversations for this bot (flows + trained)
                  const botFlows = flows.filter(f => f.bot_id === botId && f.is_active !== false);
                  const botTrainedCount = trainedConversations.filter(c => c.bot_id === botId || (!c.bot_id || c.bot_id === 'default')).length;
                  const totalBotConvs = botFlows.length + botTrainedCount;

                  return (
                    <button
                      key={botId}
                      onClick={() => {
                        setSelectedBot(botId);
                        saveUserPreferences({ preferred_bot: botId });
                      }}
                      className={`w-full p-1 rounded-lg transition-all duration-200 ${isSelected
                        ? `${colors.selected} border-2 ${colors.selected.includes('pink') ? 'border-pink-400' : colors.selected.includes('blue') ? 'border-blue-400' : 'border-gray-400'}`
                        : isDarkMode ? 'hover:bg-gray-700 border-2 border-transparent' : 'hover:bg-gray-100 border-2 border-transparent'
                      }`}
                    >
                      <div className="relative mx-auto w-12 h-12 sm:w-14 sm:h-14">
                        {/* Avatar Image */}
                        <div className="w-full h-full rounded-lg overflow-hidden flex items-center justify-center bg-gray-200/20 shadow-sm">
                          {bot.avatarUrl ? (
                            <img src={bot.avatarUrl} alt={bot.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                          ) : (
                            <span className="text-2xl sm:text-3xl">{bot.icon}</span>
                          )}
                        </div>
                        {/* Conversation count badge - outside container, visible on top */}
                        {totalBotConvs > 0 && (
                          <span className={`absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 text-[10px] font-bold rounded-full flex items-center justify-center text-white shadow-lg border-2 border-white ${colors.dot}`}>
                            {totalBotConvs}
                          </span>
                        )}
                      </div>
                      <p className={`text-[9px] mt-1 font-medium truncate text-center ${isSelected ? (isDarkMode ? 'text-white' : 'text-gray-800') : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {bot.name}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* RIGHT SIDE - Conversations */}
            <div className="flex-1 p-3 sm:p-4 min-w-0 max-h-[500px] overflow-y-auto">
              {!selectedBot ? (
                <div className={`text-center py-12`}>
                  <div className="text-4xl mb-3">👈</div>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{t('chat_select_partner')}</p>
                </div>
              ) : (
                <>
                  {/* Selected Bot Header */}
                  <div className={`flex items-center gap-3 mb-3 pb-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center bg-gray-200/20">
                      {bots[selectedBot]?.avatarUrl ? (
                        <img src={bots[selectedBot].avatarUrl} alt={bots[selectedBot].name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-2xl">{bots[selectedBot]?.icon}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{bots[selectedBot]?.name}</h3>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {filteredFlows.length + trainedConversations.filter(c => c.bot_id === selectedBot || (!c.bot_id || c.bot_id === 'default')).length} conversation{(filteredFlows.length + trainedConversations.filter(c => c.bot_id === selectedBot || (!c.bot_id || c.bot_id === 'default')).length) !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {bots[selectedBot]?.personality && (
                      <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        {bots[selectedBot].personality === 'friendly' && '😊 Friendly'}
                        {bots[selectedBot].personality === 'teacher' && '📚 Teacher'}
                        {bots[selectedBot].personality === 'playful' && '🎮 Playful'}
                        {bots[selectedBot].personality === 'calm' && '🧘 Calm'}
                        {bots[selectedBot].personality === 'professional' && '💼 Professional'}
                        {bots[selectedBot].personality === 'energetic' && '⚡ Energetic'}
                      </span>
                    )}
                  </div>
                  
                  {/* Difficulty Filter Tabs */}
                  <div className={`flex gap-1 mb-3 p-1 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                    <button
                      onClick={() => setDifficultyFilter('all')}
                      className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                        difficultyFilter === 'all'
                          ? isDarkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-800 shadow-sm'
                          : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      All ({difficultyCounts.all})
                    </button>
                    <button
                      onClick={() => setDifficultyFilter('Beginner')}
                      className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                        difficultyFilter === 'Beginner'
                          ? 'bg-green-500 text-white shadow-sm'
                          : isDarkMode ? 'text-green-400 hover:bg-green-500/20' : 'text-green-600 hover:bg-green-50'
                      }`}
                    >
                      🟢 {difficultyCounts.Beginner}
                    </button>
                    <button
                      onClick={() => setDifficultyFilter('Intermediate')}
                      className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                        difficultyFilter === 'Intermediate'
                          ? 'bg-yellow-500 text-white shadow-sm'
                          : isDarkMode ? 'text-yellow-400 hover:bg-yellow-500/20' : 'text-yellow-600 hover:bg-yellow-50'
                      }`}
                    >
                      🟡 {difficultyCounts.Intermediate}
                    </button>
                    <button
                      onClick={() => setDifficultyFilter('Advanced')}
                      className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                        difficultyFilter === 'Advanced'
                          ? 'bg-red-500 text-white shadow-sm'
                          : isDarkMode ? 'text-red-400 hover:bg-red-500/20' : 'text-red-600 hover:bg-red-50'
                      }`}
                    >
                      🔴 {difficultyCounts.Advanced}
                    </button>
                  </div>
                  
                  {/* Conversations List */}
                  {flowsLoading ? (
                    <div className="space-y-3">
                      <SkeletonCard isDarkMode={isDarkMode} />
                      <SkeletonCard isDarkMode={isDarkMode} />
                    </div>
                  ) : filteredFlows.length === 0 && trainedConversations.filter(c => c.bot_id === selectedBot || (!c.bot_id || c.bot_id === 'default')).length === 0 ? (
                    <div className={`text-center py-8 rounded-xl ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                      <div className="text-3xl mb-2">🔍</div>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {difficultyFilter === 'all'
                          ? `No conversations for this partner`
                          : `No ${difficultyFilter} conversations`}
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {difficultyFilter !== 'all' ? 'Try selecting "All" or another level' : 'Select a different partner'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredFlows.map(flow => {
                        const isCompleted = userProgress?.completed_flows?.includes(flow.id);
                        return (
                          <button
                            key={flow.id}
                            onClick={() => startConversation(flow)}
                            disabled={!flow.is_free && !hasPaidPlan}
                            className={`w-full text-left p-3 rounded-xl transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] relative ${
                              isDarkMode 
                                ? 'bg-gray-700/50 hover:bg-gray-700 border border-gray-600/50' 
                                : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                            } ${!flow.is_free && !hasPaidPlan ? 'opacity-60' : ''}`}
                          >
                            {isCompleted && (
                              <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white text-[10px] shadow">
                                ✓
                              </div>
                            )}
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                flow.difficulty === 'Beginner' ? 'bg-green-500/20 text-green-500' :
                                flow.difficulty === 'Intermediate' ? 'bg-yellow-500/20 text-yellow-500' :
                                'bg-red-500/20 text-red-500'
                              }`}>
                                <span className="text-lg">💬</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className={`font-semibold text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
                                      dir={scriptType === 'arabic' && flow.name_arabic ? 'rtl' : 'ltr'}>
                                    {scriptType === 'arabic' && flow.name_arabic ? flow.name_arabic : flow.name}
                                  </h4>
                                  {!flow.is_free && (
                                    <span className="px-1.5 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[9px] rounded font-bold flex-shrink-0">
                                      PRO
                                    </span>
                                  )}
                                </div>
                                <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                                   dir={scriptType === 'arabic' && flow.description_arabic ? 'rtl' : 'ltr'}>
                                  {scriptType === 'arabic' && flow.description_arabic ? flow.description_arabic : flow.description}
                                </p>
                                <div className="flex gap-2 mt-1">
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    flow.difficulty === 'Beginner' ? (isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-600') :
                                    flow.difficulty === 'Intermediate' ? (isDarkMode ? 'bg-yellow-900/50 text-yellow-400' : 'bg-yellow-100 text-yellow-600') :
                                    (isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-600')
                                  }`}>
                                    {flow.difficulty}
                                  </span>
                                  <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    ⏱️ {flow.estimated_duration || 5}m
                                  </span>
                                </div>
                              </div>
                              <div className={`text-lg ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>▶</div>
                            </div>
                          </button>
                        );
                      })}

                      {/* Trained Conversations — filtered by selected bot */}
                      {(() => {
                        const botTrained = trainedConversations.filter(c =>
                          c.bot_id === selectedBot || (!c.bot_id || c.bot_id === 'default')
                        );
                        return botTrained.length > 0 && (
                        <>
                          <div className={`mt-4 mb-2 flex items-center gap-2`}>
                            <div className={`h-px flex-1 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                            <span className={`text-xs font-medium px-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>🎓 Trained</span>
                            <div className={`h-px flex-1 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                          </div>
                          {botTrained.map(conv => (
                            <button
                              key={conv._id}
                              onClick={() => startTrainedConversation(conv)}
                              className={`w-full text-left p-3 rounded-xl transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] relative ${
                                isDarkMode
                                  ? 'bg-purple-900/20 hover:bg-purple-900/40 border border-purple-700/30'
                                  : 'bg-purple-50 hover:bg-purple-100 border border-purple-200'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-purple-500/20 text-purple-400">
                                  <span className="text-lg">🎓</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className={`font-semibold text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                      {conv.title}
                                    </h4>
                                    <span className="px-1.5 py-0.5 bg-purple-600 text-white text-[9px] rounded font-bold flex-shrink-0">
                                      TRAINED
                                    </span>
                                  </div>
                                  <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {conv.description || `${conv.steps?.length || 0} steps`}
                                  </p>
                                </div>
                                <div className={`text-lg ${isDarkMode ? 'text-purple-500' : 'text-purple-400'}`}>▶</div>
                              </div>
                            </button>
                          ))}
                        </>
                      );
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(ConversationChallengePlayer);
